/**
 * Enums e constantes de valores fechados do domínio.
 *
 * Fonte única de verdade — schemas Zod em `../schemas.ts` derivam desses valores
 * via `z.enum([...ALL])` e código de negócio importa daqui. Nunca usar string
 * literal solta (ex.: 'OWNER') no código; sempre TenantRole.OWNER.
 */

export const TenantRole = {
  OWNER: 'OWNER',
  MANAGER: 'MANAGER',
  STAFF: 'STAFF',
} as const;
export type TenantRole = (typeof TenantRole)[keyof typeof TenantRole];
export const ALL_TENANT_ROLES = Object.values(TenantRole) as [TenantRole, ...TenantRole[]];

/**
 * Sprint 11 — Sub-roles de super-admin no admin web. Substitui o claim
 * binário `superAdmin: true` por um claim granular `adminRole`. O claim
 * antigo continua existindo como shorthand "tem alguma role" para
 * preservar compat até toda a base ser migrada.
 *
 * Responsabilidades (matriz completa em `docs/permissions.md`):
 * - `ROOT`: tudo + gerencia outros admins (set/remove role).
 * - `OPS`: tenants, billing (leitura), audit, BI. Suspende/reativa.
 *   Sem mutar billing. Sem set/remove role.
 * - `FINANCE`: billing completo (mudar plano, registrar pagamento,
 *   editar catálogo, refund, audit financeiro). Sem suspender, sem set role.
 * - `SUPPORT`: leitura de tenants + audit + BI. Sem mutação.
 *
 * Marketing usa `OPS` (leitura de BI suficiente).
 */
export const AdminRole = {
  ROOT: 'ROOT',
  OPS: 'OPS',
  FINANCE: 'FINANCE',
  SUPPORT: 'SUPPORT',
} as const;
export type AdminRole = (typeof AdminRole)[keyof typeof AdminRole];
export const ALL_ADMIN_ROLES = Object.values(AdminRole) as [AdminRole, ...AdminRole[]];

/**
 * Países suportados (Fase 11). Define jurisdição fiscal do tenant — fixado
 * pelo telefone OTP do criador da Matriz e não editável depois (mudar país
 * exige criar nova Matriz). Cada país define a forma do `taxId` correspondente.
 */
export const Country = {
  BR: 'BR',
  MX: 'MX',
  US: 'US',
  CA: 'CA',
  IT: 'IT',
} as const;
export type Country = (typeof Country)[keyof typeof Country];
export const ALL_COUNTRIES = Object.values(Country) as [Country, ...Country[]];

/**
 * Estreita o país de um tenant (qualquer ISO alpha-2 desde a #942) para um dos
 * mercados comerciais de `Country`, ou `null` quando está fora deles.
 *
 * Origem: #942 — 2026-10-12 12:05 +0200.
 *
 * Por quê: o tenant do IAP pode ser `JP-…`. Tabelas indexadas por `Country`
 * (máscara de ID fiscal, preços, fusos) não têm entrada para ele — quem as
 * consulta passa por aqui e trata o `null` (sem máscara, fallback de locale).
 *
 * Usado por: web `apps/app` (TenantBasicsCard, editores de usuário e
 * fornecedor), `packages/ui-shared` (LabelPreview), `countryDefaults.ts`.
 */
export function marketCountryOf(code: string | null | undefined): Country | null {
  if (code === null || code === undefined) return null;
  return (ALL_COUNTRIES as readonly string[]).includes(code) ? (code as Country) : null;
}

/**
 * Canais de OTP suportados. Email não usa Twilio — vai por Firebase Auth
 * (sendEmailVerification / Email Link). WhatsApp fora de escopo na Fase 11.
 */
export const OtpChannel = {
  SMS: 'SMS',
} as const;
export type OtpChannel = (typeof OtpChannel)[keyof typeof OtpChannel];
export const ALL_OTP_CHANNELS = Object.values(OtpChannel) as [OtpChannel, ...OtpChannel[]];

export const TenantKind = {
  RESTAURANT: 'RESTAURANT',
  BAR: 'BAR',
  HOTEL: 'HOTEL',
  CATERING: 'CATERING',
  BAKERY: 'BAKERY',
  HOSPITAL: 'HOSPITAL',
  EVENTS: 'EVENTS',
  ICE_CREAM: 'ICE_CREAM',
  SUPERMARKET: 'SUPERMARKET',
  OTHER: 'OTHER',
} as const;
export type TenantKind = (typeof TenantKind)[keyof typeof TenantKind];
export const ALL_TENANT_KINDS = Object.values(TenantKind) as [TenantKind, ...TenantKind[]];

export const Storage = {
  AMBIENT: 'AMBIENT',
  REFRIGERATED: 'REFRIGERATED',
  FROZEN: 'FROZEN',
} as const;
export type Storage = (typeof Storage)[keyof typeof Storage];
export const ALL_STORAGES = Object.values(Storage) as [Storage, ...Storage[]];

export const ValidityKind = {
  PRIMARY: 'PRIMARY',
  OPENED: 'OPENED',
  MANIPULATED: 'MANIPULATED',
  THAWED: 'THAWED',
  COOKED: 'COOKED',
} as const;
export type ValidityKind = (typeof ValidityKind)[keyof typeof ValidityKind];
export const ALL_VALIDITY_KINDS = Object.values(ValidityKind) as [ValidityKind, ...ValidityKind[]];

export const ValidityOrigin = {
  PRODUCTION_DATE: 'PRODUCTION_DATE',
  OPENED_AT: 'OPENED_AT',
  THAWED_AT: 'THAWED_AT',
  COOKED_AT: 'COOKED_AT',
} as const;
export type ValidityOrigin = (typeof ValidityOrigin)[keyof typeof ValidityOrigin];
export const ALL_VALIDITY_ORIGINS = Object.values(ValidityOrigin) as [ValidityOrigin, ...ValidityOrigin[]];

export const PrintProtocol = {
  TSPL: 'TSPL',
  CPCL: 'CPCL',
  ZPL: 'ZPL',
  ESC_POS: 'ESC_POS',
} as const;
export type PrintProtocol = (typeof PrintProtocol)[keyof typeof PrintProtocol];
export const ALL_PRINT_PROTOCOLS = Object.values(PrintProtocol) as [PrintProtocol, ...PrintProtocol[]];

/**
 * Como a velocidade de impressão (`speed`, TSPL `SPEED`) é decidida (#374).
 *
 * - `AUTO`: o app calcula, a cada impressão, a maior velocidade que não pausa
 *   para o **formato da etiqueta** que vai ao papel — o do template escolhido,
 *   não o do perfil. O `speed` do documento é ignorado ao imprimir.
 * - `MANUAL`: o `speed` do documento vai como está.
 *
 * Documento sem o campo é anterior a esta decisão: ver
 * `resolvePrinterSpeedMode` (`printerSpeed.ts`) para a regra de leitura, que
 * Android e iOS espelham.
 */
export const PrinterSpeedMode = {
  AUTO: 'AUTO',
  MANUAL: 'MANUAL',
} as const;
export type PrinterSpeedMode = (typeof PrinterSpeedMode)[keyof typeof PrinterSpeedMode];
export const ALL_PRINTER_SPEED_MODES = Object.values(PrinterSpeedMode) as [
  PrinterSpeedMode,
  ...PrinterSpeedMode[],
];

/**
 * Como uma impressora é identificada no documento `printers/{id}` (item B11 da
 * #106).
 *
 * Existe porque a identidade **não é a mesma coisa em toda plataforma**. O
 * Android conhece o MAC do rádio e o usa como id do documento desde sempre. O
 * iOS não: a Apple não expõe o endereço Bluetooth a apps de terceiros, e o que
 * o CoreBluetooth entrega é um identificador que *aquela instalação* recebeu
 * para aquele periférico. Guardar isso como se fosse MAC criaria uma impressora
 * nova a cada reinstalação, e ninguém perceberia até o contador de etiquetas
 * aparecer dividido em dois.
 *
 * - `MAC` — o endereço do rádio. Android, e é o que todo documento anterior à
 *   Fase 8 significa.
 * - `BLE_SERIAL` — número de série lido do serviço de informação do aparelho
 *   (GATT `180A`/`2A25`). Atravessa aparelhos: o mesmo valor no iPhone e no
 *   iPad.
 * - `LOCAL_ANCHOR` — só o atalho de reconexão daquela instalação, usado quando
 *   o firmware não devolve número de série (clones baratos devolvem branco).
 *   Vale em um aparelho e mais nada: a mesma impressora cadastrada noutro
 *   telefone vira outro documento. É decisão do owner (2026-09-10) aceitar isso
 *   em vez de recusar o cadastro e deixar o operador sem imprimir.
 *
 * O membro `MFI_SERIAL` que o B11 original previa não existe: a 10ª rodada de
 * decisão tirou MFi e rede de cena, e o número de série do iOS vem do GATT, não
 * do chip MFi da Apple. Chamá-lo de MFi ensinaria o erro ao próximo leitor.
 */
export const PrinterIdentityType = {
  MAC: 'MAC',
  BLE_SERIAL: 'BLE_SERIAL',
  LOCAL_ANCHOR: 'LOCAL_ANCHOR',
} as const;
export type PrinterIdentityType = (typeof PrinterIdentityType)[keyof typeof PrinterIdentityType];
export const ALL_PRINTER_IDENTITY_TYPES = Object.values(PrinterIdentityType) as [
  PrinterIdentityType,
  ...PrinterIdentityType[],
];

/**
 * Por onde os bytes chegam à impressora.
 *
 * `BT_CLASSIC_SPP` é o Android (RFCOMM, congelado). `BLE_GATT` é o iOS, desde a
 * 10ª rodada de decisão da #106 (2026-09-07): a prova de conceito mediu que as
 * impressoras do parque são dual-mode e aceitam por GATT os mesmos bytes TSPL
 * que o Android emite por SPP — e como o iOS não expõe SPP a apps de terceiros,
 * BLE é o caminho para o parque atual.
 *
 * É campo **descritivo**: diz como quem escreveu o documento se conecta, não
 * como quem o lê deve se conectar. Um iPhone conecta por BLE independentemente
 * do que estiver gravado aqui.
 */
export const PrintTransport = {
  BT_CLASSIC_SPP: 'BT_CLASSIC_SPP',
  BLE_GATT: 'BLE_GATT',
} as const;
export type PrintTransport = (typeof PrintTransport)[keyof typeof PrintTransport];
export const ALL_PRINT_TRANSPORTS = Object.values(PrintTransport) as [PrintTransport, ...PrintTransport[]];

/**
 * A avaliação do operador sobre o resultado **físico** de um lote impresso.
 *
 * É o único sinal confiável de que a etiqueta saiu certa. O app sabe o que
 * **enviou**; ele não enxerga corte nem deslocamento no papel. No Android a
 * impressora nem responde (o SPP é cego); no iOS ela responde `DONE`, mas `DONE`
 * significa "a fila esvaziou", não "saiu legível". Por isso a pergunta continua
 * sendo feita nas duas plataformas — e por isso o critério de homologação da
 * §12 da #106 é *taxa de 👎 ≤ a do Android*, que só existe se as duas
 * perguntarem a mesma coisa.
 */
export const PrintOutcome = {
  GOOD: 'GOOD',
  BAD: 'BAD',
} as const;
export type PrintOutcome = (typeof PrintOutcome)[keyof typeof PrintOutcome];
export const ALL_PRINT_OUTCOMES = Object.values(PrintOutcome) as [PrintOutcome, ...PrintOutcome[]];

/**
 * Por que o operador deu 👎.
 *
 * Os valores são os sintomas do diagnóstico TSPL
 * (`docs/diagnostico-impressao-tspl-agente-3.md`), e não categorias genéricas:
 * "cortou o QR" e "cortou o conteúdo" separam um problema de tamanho de um
 * problema de margem, e é essa distinção que torna o relatório acionável.
 *
 * Existiam só em Kotlin até a Fase 8 da #106. Passam ao contrato porque agora
 * duas plataformas escrevem no mesmo documento: se o iOS inventasse outra
 * grafia para o mesmo sintoma, o relatório de perdas se partiria em dois sem
 * ninguém ver.
 */
export const PrintBadReason = {
  CUT_QR: 'CUT_QR',
  CUT_CONTENT: 'CUT_CONTENT',
  MISALIGNED: 'MISALIGNED',
  BLANK: 'BLANK',
  NOT_ALL_PRINTED: 'NOT_ALL_PRINTED',
  OTHER: 'OTHER',
} as const;
export type PrintBadReason = (typeof PrintBadReason)[keyof typeof PrintBadReason];
export const ALL_PRINT_BAD_REASONS = Object.values(PrintBadReason) as [
  PrintBadReason,
  ...PrintBadReason[],
];

/**
 * Ciclo de vida de uma etiqueta. `DRAFT` + `PRINTED` = vigentes; `CONSUMED` +
 * `DISCARDED` + `EXPIRED` + `MIXED` = histórico (baixadas). `DELETED` = soft-delete de
 * uma etiqueta impressa (a física ainda pode estar no estoque) — não é
 * vigente nem histórico: fica oculto das listas, mas é recuperável pelo scan
 * (QR/PIN). Transições canônicas: DRAFT → PRINTED (imprimir); PRINTED →
 * CONSUMED/DISCARDED (dar baixa); PRINTED → EXPIRED (cron); PRINTED → DELETED
 * (apagar impressa = soft-delete); DELETED → PRINTED (recuperar); DELETED →
 * CONSUMED/DISCARDED (recuperar e dar baixa). DRAFT continua com delete físico.
 *
 * Prazo de recuperação (decisão de produto): uma `DELETED` preserva o `ttlAt`
 * herdado de quando era `PRINTED` (`expiresAtMillis + 12 meses` — issue #237,
 * era 90d), então a TTL policy nativa apaga o doc ~12 meses após a validade
 * original — intencional: passada a validade + retenção a etiqueta física já
 * venceu e não há o que recuperar.
 *
 * `MIXED` (épico #323): etiqueta zerada por **dois ou mais desfechos
 * diferentes** (ex.: 9 kg consumidos + 11 kg descartados; ou 5 kg consumidos e
 * o resto vencido). É histórico, como os outros três terminais. O status
 * fechado é função dos desfechos presentes no ledger `baixas[]`, **não** do
 * último movimento — ver `closedStatusFor` em `labelQuantity.ts`.
 *
 * Cronologia: a F0 (#330) ensinou os clientes a LER o valor; a F1 (#324) deu
 * ao trigger a capacidade de DERIVÁ-LO; a F3 (#325/#326) pôs os clientes a
 * PRODUZI-LO — desde ela a mista é o resultado esperado de uma baixa parcial,
 * não um sintoma. O alarme `label_mixed_before_rollout` que denunciava a mista
 * prematura saiu junto com o rollout (#353): um alarme que dispara por
 * construção ensina o time a ignorar o canal. `mixed` segue verificado onde
 * importa — comparação exata dos terminais e identidade do `dropped` em
 * `monitorDailyOpsHealth`. Espelhado à mão em `LabelStatus` (Kotlin).
 */
export const LabelStatus = {
  DRAFT: 'DRAFT',
  PRINTED: 'PRINTED',
  CONSUMED: 'CONSUMED',
  DISCARDED: 'DISCARDED',
  EXPIRED: 'EXPIRED',
  MIXED: 'MIXED',
  DELETED: 'DELETED',
} as const;
export type LabelStatus = (typeof LabelStatus)[keyof typeof LabelStatus];
export const ALL_LABEL_STATUSES = Object.values(LabelStatus) as [LabelStatus, ...LabelStatus[]];

/**
 * Canal pelo qual a última transição de status da etiqueta foi disparada
 * (issue #230). Gravado em `Label.statusChangedVia` pelo cliente e propagado
 * pelo trigger para `metadata.via` do `activity_log`.
 *
 * Antes disso o canal só saía por heurística — a auditoria do incidente do
 * Caffe Faria teve de inferir "82,5% inline no sheet" pela ausência de um
 * `BARCODE_SCANNED` imediatamente antes da baixa, o que só funciona dentro do
 * TTL do activity_log e não distingue PIN de scan.
 *
 * - `SCAN`   — QR da etiqueta lido pela câmera.
 * - `PIN`    — código curto digitado (fallback quando o QR raspa).
 * - `INLINE` — botão no card do `LabelGroupSheet`.
 * - `BULK`   — seleção múltipla (Android).
 * - `DETAIL` — tela de detalhe da etiqueta.
 * - `UNDO`   — desfazer baixa (terminal → PRINTED).
 * - `API`    — webhook de entrada de um parceiro (#803, 2026-10-04): o autor
 *   aparece como "API". Só o servidor grava; as rules aceitam o valor no doc
 *   (`isValidStatusChangedVia`) para uma edição posterior do app não ser
 *   recusada, mas nunca numa entrada de ledger escrita por cliente.
 * - `NFC`    — tag NFC da etiqueta (módulo `MOD_NFC_TAGS`, #900/#901,
 *   2026-10-09): a baixa feita no diálogo da etiqueta aberto pela tag
 *   encostada (leitura em segundo plano na tela Etiquetas, ou a tag que abriu
 *   o app), gravada pelo cliente como `SCAN`/`PIN`, e a emissão de uma etiqueta só digital — o
 *   `DRAFT → PRINTED` que o callable `bindNfcTag` faz ao vincular a tag, sem
 *   impressão. As rules aceitam o valor do cliente na baixa e na entrada de
 *   ledger de movimento.
 *
 * Ausente/`null` em etiquetas de clientes anteriores ao campo.
 */
export const StatusChangedVia = {
  SCAN: 'SCAN',
  PIN: 'PIN',
  INLINE: 'INLINE',
  BULK: 'BULK',
  DETAIL: 'DETAIL',
  UNDO: 'UNDO',
  API: 'API',
  NFC: 'NFC',
} as const;
export type StatusChangedVia = (typeof StatusChangedVia)[keyof typeof StatusChangedVia];
export const ALL_STATUS_CHANGED_VIA = Object.values(StatusChangedVia) as [
  StatusChangedVia,
  ...StatusChangedVia[],
];

/**
 * Onde uma entrada do ledger de baixas nasceu (épico #323).
 *
 * Vive aqui, e não em `labelBaixaPatch.ts`/`labelFieldContract.ts`, porque os
 * mesmos quatro valores precisam ser alcançáveis por consumidores que **não**
 * podem importar zod: a suíte de Firestore Rules (pacote npm isolado) deriva a
 * lista daqui para provar que `firestore.rules:167` aceita exatamente estes.
 * Antes disso os valores estavam escritos à mão em quatro arquivos
 * independentes, e acrescentar `IOS` (#106) exigiu editar os quatro.
 *
 * `SERVER` é o fallback de leitura: um valor gravado por um cliente mais novo
 * não pode reprovar o array e fazer a etiqueta inteira sumir da lista.
 */
export const BaixaPlatform = {
  ANDROID: 'ANDROID',
  WEB: 'WEB',
  SERVER: 'SERVER',
  IOS: 'IOS',
} as const;
export type BaixaPlatform = (typeof BaixaPlatform)[keyof typeof BaixaPlatform];
export const ALL_BAIXA_PLATFORMS = Object.values(BaixaPlatform) as [
  BaixaPlatform,
  ...BaixaPlatform[],
];

/** Fallback de leitura de `BaixaPlatform` — ver o comentário acima. */
export const BAIXA_PLATFORM_FALLBACK: BaixaPlatform = BaixaPlatform.SERVER;

// ------------------------------------------------------------
// Activity log
// ------------------------------------------------------------

/**
 * Sprint 18: branches deixaram de existir. `ActivityScope` ficou redundante
 * (só `TENANT`) — mantido como enum unitário porque o tipo já está espalhado
 * pelos schemas e o cliente Android lê o campo. Pode ser removido em uma
 * limpeza futura.
 */
export const ActivityScope = {
  TENANT: 'TENANT',
} as const;
export type ActivityScope = (typeof ActivityScope)[keyof typeof ActivityScope];
export const ALL_ACTIVITY_SCOPES = Object.values(ActivityScope) as [ActivityScope, ...ActivityScope[]];

export const EntityType = {
  TENANT: 'TENANT',
  PRODUCT: 'PRODUCT',
  LABEL: 'LABEL',
  LABEL_TEMPLATE: 'LABEL_TEMPLATE',
  PRINTER: 'PRINTER',
  BARCODE: 'BARCODE',
  USER: 'USER',
  RESPONSIBLE: 'RESPONSIBLE',
  SUPPLIER: 'SUPPLIER',
  // #336 — módulo Checklists (MOD_CHECKLISTS). Entram na F1 (contrato); quem
  // grava o `activity_log` com elas é o trigger da F2.
  CHECKLIST: 'CHECKLIST',
  CHECKLIST_RUN: 'CHECKLIST_RUN',
  CORRECTIVE_ACTION: 'CORRECTIVE_ACTION',
} as const;
export type EntityType = (typeof EntityType)[keyof typeof EntityType];
export const ALL_ENTITY_TYPES = Object.values(EntityType) as [EntityType, ...EntityType[]];

/**
 * Ações registradas no `activity_log`. Novas ações devem ser adicionadas aqui
 * primeiro; o cliente Android espelha este enum em `ActivityAction.kt`.
 */
export const ActivityAction = {
  // Tenant
  TENANT_CREATED: 'TENANT_CREATED',
  TENANT_UPDATED: 'TENANT_UPDATED',
  TENANT_SUSPENDED: 'TENANT_SUSPENDED',
  TENANT_REACTIVATED: 'TENANT_REACTIVATED',
  // Product
  PRODUCT_CREATED: 'PRODUCT_CREATED',
  PRODUCT_UPDATED: 'PRODUCT_UPDATED',
  PRODUCT_ACTIVATED: 'PRODUCT_ACTIVATED',
  PRODUCT_DEACTIVATED: 'PRODUCT_DEACTIVATED',
  PRODUCT_DELETED: 'PRODUCT_DELETED',
  PRODUCT_MIN_STOCK_UPDATED: 'PRODUCT_MIN_STOCK_UPDATED',
  // Label
  LABEL_CREATED: 'LABEL_CREATED',
  LABEL_DRAFT_CREATED: 'LABEL_DRAFT_CREATED',
  LABEL_DRAFT_DELETED: 'LABEL_DRAFT_DELETED',
  LABEL_PRINTED: 'LABEL_PRINTED',
  LABEL_PRINT_FAILED: 'LABEL_PRINT_FAILED',
  LABEL_CONSUMED: 'LABEL_CONSUMED',
  LABEL_DISCARDED: 'LABEL_DISCARDED',
  LABEL_EXPIRED: 'LABEL_EXPIRED',
  LABEL_REUSED: 'LABEL_REUSED',
  // Soft-delete de etiqueta impressa (issue #148/#149): apagar PRINTED vira
  // update status→DELETED (não remove o doc); recuperação volta a PRINTED.
  LABEL_DELETED: 'LABEL_DELETED',
  LABEL_RECOVERED: 'LABEL_RECOVERED',
  // Desfazer baixa (issue #230): CONSUMED/DISCARDED/EXPIRED → PRINTED, por
  // OWNER/MANAGER. É ação PRÓPRIA, não `LABEL_RECOVERED`: aquela é
  // DELETED→PRINTED (desfazer uma exclusão), enquanto esta reverte um evento
  // de negócio JÁ CONTABILIZADO em `daily_ops` — que o trigger compensa com
  // delta negativo. Misturar as duas inviabilizaria auditar "quantas baixas
  // foram desfeitas" e faria a compensação disparar no caso errado.
  LABEL_UNDONE: 'LABEL_UNDONE',
  // Baixa parcial (épico #323, fase F1 — issue #324). Uma etiqueta de 20 kg
  // com 9 kg consumidos NÃO muda de status: continua `PRINTED` com 11 kg
  // vigentes. Sem ação própria, o movimento não apareceria no Registro de
  // atividade — o trigger só gravava linha em transição de status.
  LABEL_PARTIAL_CONSUMED: 'LABEL_PARTIAL_CONSUMED',
  LABEL_PARTIAL_DISCARDED: 'LABEL_PARTIAL_DISCARDED',
  // Fechamento por DOIS OU MAIS desfechos (`MIXED`): 9 kg consumidos + 11 kg
  // descartados, ou 5 kg consumidos e o resto vencido.
  LABEL_MIXED: 'LABEL_MIXED',
  // Anomalia auditável: a soma das baixas passou da quantidade original
  // (dois cozinheiros pesando o mesmo saco offline — épico §5.1). O fato é
  // registrado e sinalizado, nunca apagado; quem corrige é o gerente, por
  // estorno.
  LABEL_OVERRUN: 'LABEL_OVERRUN',
  // Movimento aceito pela exceção de reconciliação offline que só chegou ao
  // servidor mais de 24 h depois da validade. O `atMillis` declarado diz que
  // aconteceu antes de vencer; o horário de ingestão diz que a informação
  // chegou muito tarde. Vai para revisão do gerente.
  LABEL_LATE_AFTER_EXPIRY: 'LABEL_LATE_AFTER_EXPIRY',
  // Printer
  PRINTER_CREATED: 'PRINTER_CREATED',
  PRINTER_UPDATED: 'PRINTER_UPDATED',
  PRINTER_DELETED: 'PRINTER_DELETED',
  PRINTER_CONNECTED: 'PRINTER_CONNECTED',
  PRINTER_DISCONNECTED: 'PRINTER_DISCONNECTED',
  PRINTER_TEST_PRINTED: 'PRINTER_TEST_PRINTED',
  // Label template
  LABEL_TEMPLATE_CREATED: 'LABEL_TEMPLATE_CREATED',
  LABEL_TEMPLATE_UPDATED: 'LABEL_TEMPLATE_UPDATED',
  LABEL_TEMPLATE_DELETED: 'LABEL_TEMPLATE_DELETED',
  // Responsible (Sprint 27)
  RESPONSIBLE_CREATED: 'RESPONSIBLE_CREATED',
  RESPONSIBLE_ACTIVATED: 'RESPONSIBLE_ACTIVATED',
  RESPONSIBLE_DEACTIVATED: 'RESPONSIBLE_DEACTIVATED',
  RESPONSIBLE_DEFAULT_CHANGED: 'RESPONSIBLE_DEFAULT_CHANGED',
  RESPONSIBLE_DEFAULT_PROMOTED: 'RESPONSIBLE_DEFAULT_PROMOTED',
  RESPONSIBLE_DELETED: 'RESPONSIBLE_DELETED',
  // Supplier (issue #193 — client-write; audit gravado pelo trigger
  // `onSupplierWritten`)
  SUPPLIER_CREATED: 'SUPPLIER_CREATED',
  SUPPLIER_UPDATED: 'SUPPLIER_UPDATED',
  SUPPLIER_ACTIVATED: 'SUPPLIER_ACTIVATED',
  SUPPLIER_DEACTIVATED: 'SUPPLIER_DEACTIVATED',
  SUPPLIER_DELETED: 'SUPPLIER_DELETED',
  // Checklists (#336 F1). Só as mudanças de configuração e de supervisão vão
  // para o mural — a execução em si tem histórico próprio no módulo, e uma
  // linha por resposta afogaria o Registro de atividade. Conjunto conferido na
  // F2, quando o trigger passa a gravar. Build antiga cai em `TENANT_UPDATED`
  // (fallback de leitura), o que o §4 da #336 já prevê.
  CHECKLIST_CREATED: 'CHECKLIST_CREATED',
  CHECKLIST_UPDATED: 'CHECKLIST_UPDATED',
  CHECKLIST_DEACTIVATED: 'CHECKLIST_DEACTIVATED',
  CHECKLIST_RANGES_UPDATED: 'CHECKLIST_RANGES_UPDATED',
  CHECKLIST_RUN_DISMISSED: 'CHECKLIST_RUN_DISMISSED',
  CHECKLIST_RUN_VERIFIED: 'CHECKLIST_RUN_VERIFIED',
  // Ação corretiva (#336 F2, auditoria pré-implementação, achado 14): o
  // `EntityType.CORRECTIVE_ACTION` existia sem nenhuma ação para gravar.
  // Gravadas por `onCorrectiveActionWritten`; uma linha por mudança de estado,
  // não por edição de campo.
  CORRECTIVE_ACTION_CREATED: 'CORRECTIVE_ACTION_CREATED',
  CORRECTIVE_ACTION_RESOLVED: 'CORRECTIVE_ACTION_RESOLVED',
  CORRECTIVE_ACTION_VERIFIED: 'CORRECTIVE_ACTION_VERIFIED',
  CORRECTIVE_ACTION_REOPENED: 'CORRECTIVE_ACTION_REOPENED',
  CORRECTIVE_ACTION_CANCELLED: 'CORRECTIVE_ACTION_CANCELLED',
  // Operacionais
  BARCODE_SCANNED: 'BARCODE_SCANNED',
  /**
   * Falha de leitura de QR/barcode no Android (etiqueta ilegível, QR de outro
   * sistema, etiqueta apagada). Só as falhas acionáveis pelo dono chegam aqui —
   * problema de device vai para Crashlytics/PostHog. Ver `ScanFailureReporter`.
   */
  BARCODE_SCAN_FAILED: 'BARCODE_SCAN_FAILED',
  USER_SIGNED_IN: 'USER_SIGNED_IN',
  // Verificação de identidade (Fase 11)
  PHONE_OTP_REQUESTED: 'PHONE_OTP_REQUESTED',
  PHONE_OTP_VERIFIED: 'PHONE_OTP_VERIFIED',
  PHONE_OTP_FAILED: 'PHONE_OTP_FAILED',
  EMAIL_VERIFIED: 'EMAIL_VERIFIED',
  USER_INVITED_TO_TENANT: 'USER_INVITED_TO_TENANT',
  USER_TENANT_ROLE_CHANGED: 'USER_TENANT_ROLE_CHANGED',
  USER_REMOVED_FROM_TENANT: 'USER_REMOVED_FROM_TENANT',
  // O dono religa o acesso de quem havia se desativado pelo app. Fica no mural
  // do estabelecimento porque é mudança de quem consegue trabalhar ali — a
  // mesma razão de `USER_REMOVED_FROM_TENANT` estar aqui.
  USER_ACCESS_REACTIVATED: 'USER_ACCESS_REACTIVATED',
  STAFF_CREATED_WITH_CREDENTIALS: 'STAFF_CREATED_WITH_CREDENTIALS',
  MEMBER_PASSWORD_CHANGED: 'MEMBER_PASSWORD_CHANGED',
  MEMBER_PHOTO_CHANGED: 'MEMBER_PHOTO_CHANGED',
  // Billing (Sprint 16)
  PLAN_ACTIVATED: 'PLAN_ACTIVATED',
  INVOICE_CREATED: 'INVOICE_CREATED',
  INVOICE_PAID: 'INVOICE_PAID',
  INVOICE_CANCELLED: 'INVOICE_CANCELLED',
  INVOICE_OVERDUE: 'INVOICE_OVERDUE',
  PAYMENT_RECORDED: 'PAYMENT_RECORDED',
  BILLING_OVERDUE: 'BILLING_OVERDUE',
  BILLING_SETTINGS_UPDATED: 'BILLING_SETTINGS_UPDATED',
  PLAN_PRICE_UPDATED: 'PLAN_PRICE_UPDATED',
  BILLING_CYCLE_CHANGED: 'BILLING_CYCLE_CHANGED',
} as const;
export type ActivityAction = (typeof ActivityAction)[keyof typeof ActivityAction];
export const ALL_ACTIVITY_ACTIONS = Object.values(ActivityAction) as [ActivityAction, ...ActivityAction[]];

/**
 * Actor sintético para ações sem usuário humano (ex.: LABEL_EXPIRED disparado
 * pelo cron `scanExpiringLabels`).
 */
export const SYSTEM_ACTOR_UID = '__system__';

/**
 * Chave de bucket do ator "Sistema" em `daily_ops.byActor` (aba Baixadas). NÃO
 * usar `SYSTEM_ACTOR_UID` como chave de mapa: o Firestore reserva nomes de campo
 * que casam `__.*__` e rejeita a escrita (`field name '__system__' is reserved`).
 * O valor REAL `SYSTEM_ACTOR_UID` continua em `label.statusChangedByUid` (é o que
 * o drilldown consulta); só a chave do mapa é sanitizada.
 */
export const SYSTEM_ACTOR_BUCKET = 'system';

// ------------------------------------------------------------
// Product taxonomies
// ------------------------------------------------------------

// Taxonomia revisada (2026-05-21): set fechado orientado ao food-service
// brasileiro. Códigos em inglês (convenção do projeto); rótulos exibidos vêm
// do i18n (`categoryOptions.*` web / `category_*` Android). Produtos legados
// migram via `scripts/migrate-product-categories.ts`.
export const ProductCategory = {
  POULTRY: 'POULTRY', BEEF: 'BEEF', PORK: 'PORK', LAMB: 'LAMB',
  FISH: 'FISH', SEAFOOD: 'SEAFOOD', DELI: 'DELI',
  DAIRY: 'DAIRY', EGGS: 'EGGS', PRODUCE: 'PRODUCE',
  PASTA: 'PASTA', BAKERY: 'BAKERY', DESSERT: 'DESSERT',
  SAUCE: 'SAUCE', FILLING: 'FILLING',
  CLEANING: 'CLEANING', SUPPLIES: 'SUPPLIES', GROCERY: 'GROCERY',
  OTHER: 'OTHER',
} as const;
export type ProductCategory = (typeof ProductCategory)[keyof typeof ProductCategory];
export const ALL_PRODUCT_CATEGORIES = Object.values(ProductCategory) as [ProductCategory, ...ProductCategory[]];

export const Allergen = {
  GLUTEN: 'GLUTEN', CRUSTACEAN: 'CRUSTACEAN', EGG: 'EGG', FISH: 'FISH',
  PEANUT: 'PEANUT', SOY: 'SOY', MILK: 'MILK', LACTOSE: 'LACTOSE',
  NUTS: 'NUTS', CELERY: 'CELERY', MUSTARD: 'MUSTARD', SESAME: 'SESAME',
  SULPHITES: 'SULPHITES', LUPIN: 'LUPIN', MOLLUSK: 'MOLLUSK',
} as const;
export type Allergen = (typeof Allergen)[keyof typeof Allergen];
export const ALL_ALLERGENS = Object.values(Allergen) as [Allergen, ...Allergen[]];

// ------------------------------------------------------------
// Label templates (Sprint 15)
// ------------------------------------------------------------

/**
 * Escala de texto agnóstica de protocolo. Cada renderer (TSPL/CPCL/ESC-POS)
 * traduz para sua unidade nativa (multiplier, font ID, etc.).
 */
export const TextScale = {
  XSMALL: 'XSMALL',
  SMALL: 'SMALL',
  NORMAL: 'NORMAL',
  LARGE: 'LARGE',
  XLARGE: 'XLARGE',
} as const;
export type TextScale = (typeof TextScale)[keyof typeof TextScale];
export const ALL_TEXT_SCALES = Object.values(TextScale) as [TextScale, ...TextScale[]];

/**
 * Discriminador do `kind` em `TemplateBlockSchema`. Sprint 19 substituiu o
 * antigo modelo `TemplateField` + `columnsLayout` por uma pilha linear de
 * blocos top→bottom, cada um com sua própria geometria.
 */
export const BlockKind = {
  TEXT: 'Text',
  KEY_VALUE: 'KeyValue',
  INLINE_ROW: 'InlineRow',
  HORIZONTAL_RULE: 'HorizontalRule',
  BARCODE: 'Barcode',
  /** Logo do tenant (Storage `tenants/{id}/branding/logo.png`). */
  TENANT_LOGO: 'TenantLogo',
  /** Foto do produto resolvido em runtime (`Product.imageUrl`). */
  PRODUCT_IMAGE: 'ProductImage',
  /** Imagem livre carregada pelo usuário no editor de template. */
  CUSTOM_IMAGE: 'CustomImage',
} as const;
export type BlockKind = (typeof BlockKind)[keyof typeof BlockKind];
export const ALL_BLOCK_KINDS = Object.values(BlockKind) as [BlockKind, ...BlockKind[]];

/** Alinhamento horizontal de um slot de texto ou de um barcode dentro do bloco. */
export const BlockAlign = {
  LEFT: 'LEFT',
  CENTER: 'CENTER',
  RIGHT: 'RIGHT',
} as const;
export type BlockAlign = (typeof BlockAlign)[keyof typeof BlockAlign];
export const ALL_BLOCK_ALIGNS = Object.values(BlockAlign) as [BlockAlign, ...BlockAlign[]];

/**
 * Layout de um `KeyValueBlock`. `JUSTIFY` = key à esquerda, value à direita
 * (padrão de etiqueta — "Vence: 12/05"). `LEFT_LEFT` = key e value coladas
 * (formato "Vence:12/05"). `CENTER` = ambas centralizadas em uma linha.
 */
export const KvAlign = {
  LEFT_LEFT: 'LEFT_LEFT',
  JUSTIFY: 'JUSTIFY',
  CENTER: 'CENTER',
} as const;
export type KvAlign = (typeof KvAlign)[keyof typeof KvAlign];
export const ALL_KV_ALIGNS = Object.values(KvAlign) as [KvAlign, ...KvAlign[]];

/**
 * Modo de aproveitamento vertical do template. Sprint 19.
 * - `NATURAL`: respeita gaps fixos; sobra branco se conteúdo < altura.
 * - `FILL`: distribui sobra como gap extra entre blocos (ou centraliza se 1 bloco).
 * - `WARN_IF_GAP`: igual NATURAL com banner se sobra > 20%.
 */
export const VerticalFit = {
  NATURAL: 'NATURAL',
  FILL: 'FILL',
  WARN_IF_GAP: 'WARN_IF_GAP',
} as const;
export type VerticalFit = (typeof VerticalFit)[keyof typeof VerticalFit];
export const ALL_VERTICAL_FITS = Object.values(VerticalFit) as [VerticalFit, ...VerticalFit[]];

/**
 * Origem do dado em um slot de bloco. Substitui o antigo `TemplateFieldKind`
 * que era também o discriminator da `TemplateFieldSchema`. Cada kind mapeia
 * para `Product.*` / `Label.*` (inclusive o snapshot da regra: `ruleKind`,
 * `ruleDurationHours`, `ruleOrigin`, `ruleLabelText`) / `Tenant.*` em
 * runtime, ou para texto literal (`TextoLivre`), ou marca slot vazio (`Empty` —
 * usado em `InlineRow` com 1 slot).
 */
export const FieldSourceKind = {
  PRODUCT_NAME: 'ProductName',
  DESCRIPTION: 'Description',
  TIPO_ETIQUETA: 'TipoEtiqueta',
  PREPARO: 'Preparo',
  VALIDADE: 'Validade',
  LOTE: 'Lote',
  SKU: 'Sku',
  FORNECEDOR: 'Fornecedor',
  RESPONSAVEL: 'Responsavel',
  INGREDIENTES: 'Ingredientes',
  ALERGENICOS: 'Alergenicos',
  TEXTO_LIVRE: 'TextoLivre',
  TENANT_NAME: 'TenantName',
  STORAGE: 'Storage',
  RULE_LABEL: 'RuleLabel',
  DURATION: 'Duration',
  PRINTED_AT: 'PrintedAt',
  ORIGIN: 'Origin',
  UNIT_QUANTITY: 'UnitQuantity',
  EMPTY: 'Empty',
} as const;
export type FieldSourceKind = (typeof FieldSourceKind)[keyof typeof FieldSourceKind];
export const ALL_FIELD_SOURCE_KINDS = Object.values(FieldSourceKind) as [FieldSourceKind, ...FieldSourceKind[]];

/**
 * Discriminador do conteúdo de um `Slot`. Sprint 20 generaliza slot de
 * "só texto" para 3 variantes:
 *
 * - `TEXT`: caminho histórico — slot carrega `FieldSource` textual.
 * - `IMAGE`: slot carrega imagem (logo do tenant, foto do produto ou
 *   imagem livre) — composição com texto na mesma linha lógica.
 * - `BARCODE`: slot carrega glyph de código (Code128 / EAN13 / QR) —
 *   permite "QR + SKU" lado-a-lado, "código + descrição" em KeyValue.
 */
export const SlotContentKind = {
  TEXT: 'Text',
  IMAGE: 'Image',
  BARCODE: 'Barcode',
} as const;
export type SlotContentKind = (typeof SlotContentKind)[keyof typeof SlotContentKind];
export const ALL_SLOT_CONTENT_KINDS = Object.values(SlotContentKind) as [
  SlotContentKind,
  ...SlotContentKind[],
];

/**
 * Origem do bitmap de uma `SlotContent.Image`. Sprint 20.
 *
 * - `TENANT_LOGO`: resolve `Tenant.logoUrl` em runtime.
 * - `PRODUCT_IMAGE`: resolve `Product.imageUrl` em runtime; slot omitido
 *   se produto não tiver foto.
 * - `CUSTOM_IMAGE`: bitmap próprio do template (mesmo path/url pattern
 *   do bloco isolado `CustomImage`).
 */
export const SlotImageRefKind = {
  TENANT_LOGO: 'TenantLogo',
  PRODUCT_IMAGE: 'ProductImage',
  CUSTOM_IMAGE: 'CustomImage',
} as const;
export type SlotImageRefKind = (typeof SlotImageRefKind)[keyof typeof SlotImageRefKind];
export const ALL_SLOT_IMAGE_REF_KINDS = Object.values(SlotImageRefKind) as [
  SlotImageRefKind,
  ...SlotImageRefKind[],
];

/**
 * Tipos de barcode suportados em `BarcodeBlock`. `QR` é o caso comum
 * (carrega payload JSON para baixa offline); CODE128/EAN13 ficam para
 * integração com leitores 1D legados.
 */
export const BarcodeKind = {
  CODE128: 'CODE128',
  EAN13: 'EAN13',
  QR: 'QR',
} as const;
export type BarcodeKind = (typeof BarcodeKind)[keyof typeof BarcodeKind];
export const ALL_BARCODE_KINDS = Object.values(BarcodeKind) as [BarcodeKind, ...BarcodeKind[]];

/**
 * Origem do dado do barcode em runtime. `QR_PAYLOAD` é o JSON canônico de
 * `LabelQrPayload`. `SKU` puxa de `Product.sku`. `LABEL_ID` puxa de
 * `Label.id`. `CUSTOM` usa `customValue` literal do template.
 */
export const BarcodeSource = {
  SKU: 'SKU',
  LABEL_ID: 'LABEL_ID',
  QR_PAYLOAD: 'QR_PAYLOAD',
  CUSTOM: 'CUSTOM',
} as const;
export type BarcodeSource = (typeof BarcodeSource)[keyof typeof BarcodeSource];
export const ALL_BARCODE_SOURCES = Object.values(BarcodeSource) as [BarcodeSource, ...BarcodeSource[]];

export const UnitOfMeasure = {
  UNIT: 'UNIT', GRAM: 'GRAM', KILOGRAM: 'KILOGRAM',
  MILLILITER: 'MILLILITER', LITER: 'LITER',
  PACK: 'PACK', BOX: 'BOX',
} as const;
export type UnitOfMeasure = (typeof UnitOfMeasure)[keyof typeof UnitOfMeasure];
export const ALL_UNITS_OF_MEASURE = Object.values(UnitOfMeasure) as [UnitOfMeasure, ...UnitOfMeasure[]];

/**
 * Quantidade da etiqueta (`Label.quantity`) aceita casas decimais somente
 * para `KILOGRAM` e `LITER` (issue #39: "1,5 kg", "0,75 L"). As demais
 * unidades — `GRAM`, `MILLILITER`, `UNIT`, `PACK`, `BOX` — só aceitam
 * inteiros (peso/volume fracionário fino se modela escolhendo g/ml). O
 * valor é guardado sempre na própria unidade do produto (decimal, não
 * convertido pra unidade base) — sem migração de dados.
 */
export function unitAllowsDecimal(unit: UnitOfMeasure): boolean {
  return unit === UnitOfMeasure.KILOGRAM || unit === UnitOfMeasure.LITER;
}

/**
 * Máximo de casas decimais aceitas na quantidade quando a unidade permite
 * decimal (kg/L). 3 casas = precisão de 1 g (kg) / 1 ml (L). Cliente e
 * formatação compartilham esse teto.
 */
export const QUANTITY_MAX_DECIMALS = 3;

/**
 * Status operacional do tenant (controla acesso ao app). Mutável apenas
 * por callables admin (super-admin) ou pelo scheduler de billing
 * (commit 6 do Sprint 1). `SUSPENDED_BILLING` vem do scheduler quando
 * detecta atraso > graceDays. `SUSPENDED_ADMIN` é manual pelo
 * super-admin (caso fraude, abuso, etc.). `DELETED` é estado terminal
 * (mas restaurável via super-admin) — Sprint 19. Tenant DELETED some
 * de queries de operação, billing e cliente; subcoleções continuam
 * existindo até job de retenção LGPD (Sprint 13) apagar fisicamente.
 */
export const TenantStatus = {
  ACTIVE: 'ACTIVE',
  SUSPENDED_BILLING: 'SUSPENDED_BILLING',
  SUSPENDED_ADMIN: 'SUSPENDED_ADMIN',
  DELETED: 'DELETED',
} as const;
export type TenantStatus = (typeof TenantStatus)[keyof typeof TenantStatus];
export const ALL_TENANT_STATUSES = Object.values(TenantStatus) as [TenantStatus, ...TenantStatus[]];

/**
 * Sprint 13 — status do user. `ACTIVE` é o estado normal. `DELETED` é
 * soft-delete iniciado pelo titular via `deleteUserAccount` (LGPD/GDPR
 * direito de exclusão). Após `deletedAt + 30d` o sweep
 * `userRetentionSweep` anonimiza o doc e apaga o Auth account
 * (hard-delete). Usuário em `DELETED` ainda consegue fazer login (para
 * ver aviso e restaurar) — sessão nova é requerida pois revogamos
 * tokens no soft-delete.
 */
export const UserStatus = {
  ACTIVE: 'ACTIVE',
  DELETED: 'DELETED',
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];
export const ALL_USER_STATUSES = Object.values(UserStatus) as [UserStatus, ...UserStatus[]];

/**
 * Identificador do plano. Após Sprint 16, **único valor possível**: o
 * plano padrão. `PlanApplyMode`/`PlanPricingMode` foram removidos
 * junto com o catálogo dinâmico — gateway real volta na Sprint 5.
 */
export const PlanId = {
  STANDARD: 'STANDARD',
  /**
   * #942 (2026-10-12 11:56 +0200) — plano único vendido por In-App Purchase no iOS: mensal,
   * todos os módulos do catálogo inclusos (itens `IAP_BUNDLE`). O preço é o do
   * storefront da Apple (era US$ 249 na #942; desde #970, 2026-10-15, o `amount`
   * guarda o preço que a loja informou na compra — sem constante no código).
   * #969 (2026-10-14 13:31 +0200) — também o plano self-service do Android
   * (Google Play) e da Stripe (web + Android), com preço por zona. Desde
   * 2026-10-15 21:19 +0200 (#970) cada loja é dona do preço (Stripe pela
   * `lookup_key` da zona); o doc do CRM saiu.
   */
  ALL_IN_ONE: 'ALL_IN_ONE',
} as const;
export type PlanId = (typeof PlanId)[keyof typeof PlanId];
export const ALL_PLAN_IDS = Object.values(PlanId) as [PlanId, ...PlanId[]];

/**
 * Códigos de moeda ISO 4217 suportados pelo plano padrão. Sprint 16
 * reduziu para 3 — BRL (BR), USD (resto LATAM/US), EUR (futuro EU).
 */
export const Currency = {
  BRL: 'BRL',
  USD: 'USD',
  EUR: 'EUR',
} as const;
export type Currency = (typeof Currency)[keyof typeof Currency];
export const ALL_CURRENCIES = Object.values(Currency) as [Currency, ...Currency[]];

/**
 * Ciclo de cobrança da assinatura. Sprint 16: cliente escolhe entre
 * mensal e anual no onboarding (default mensal). `yearly` ganha desconto
 * de "2 meses grátis" (`monthly × 10`) por país, configurável em
 * `/config/planPrices/{country}_{cycle}`.
 */
export const BillingCycle = {
  MONTHLY: 'monthly',
  /**
   * #679 D13 — trimestral e semestral só existem na venda assistida: são
   * cobrados com `price_data.recurring { interval: 'month', interval_count: 3 | 6 }`
   * e o catálogo de lista (`/config/planPrices`) não tem Price para eles. A
   * aritmética de período mora em `monthsInCycle` — nunca um ternário
   * `YEARLY ? ano : mês`, que tratava trimestre como mês e cobrava na data
   * errada.
   */
  QUARTERLY: 'quarterly',
  SEMIANNUAL: 'semiannual',
  YEARLY: 'yearly',
} as const;
export type BillingCycle = (typeof BillingCycle)[keyof typeof BillingCycle];
export const ALL_BILLING_CYCLES = Object.values(BillingCycle) as [BillingCycle, ...BillingCycle[]];

/**
 * #679 §4.5 — tipo comercial do tenant: quem cobra, e se cobra.
 *
 * - `DIRECT`: vendido por nós e cobrado pelo nosso PSP. O único que passa pelo
 *   gate de pagamento.
 * - `COURTESY`: cliente real que não cobramos.
 * - `PARTNER`: o parceiro vende, cobra o cliente e nos repassa.
 * - `DEMO`: os tenants de demonstração compartilhados por língua.
 *
 * Nasceu como `billing_mode` no texto da issue e foi renomeado antes de
 * existir (#699, decisão a): `billing_mode` é nome de campo da própria
 * `Subscription` da Stripe, com tipo oposto (objeto × string), manipulado lado
 * a lado nos handlers de webhook.
 *
 * **Ausente = `DIRECT`, só em memória** (`commercialModeOf`). O Firestore não
 * casa igualdade com campo ausente (C5): nenhuma query por
 * `commercial_mode == 'DIRECT'` entra em produção antes do backfill da F7.
 */
export const CommercialMode = {
  DIRECT: 'DIRECT',
  COURTESY: 'COURTESY',
  PARTNER: 'PARTNER',
  DEMO: 'DEMO',
} as const;
export type CommercialMode = (typeof CommercialMode)[keyof typeof CommercialMode];
export const ALL_COMMERCIAL_MODES = Object.values(CommercialMode) as [
  CommercialMode,
  ...CommercialMode[],
];

/**
 * #679 §4.2 — de onde veio o preço de `Subscription.items[]`: do catálogo de
 * lista (self-service, tenants antigos) ou negociado item a item pelo
 * colaborador do CRM (`price_data`). Descritivo: nenhum gate o consulta.
 */
export const PricingSource = {
  CATALOG: 'CATALOG',
  CRM_SALE: 'CRM_SALE',
  /** #743 — composição negociada pelo PARCEIRO (sem gateway; ver `remit_cents`). */
  PARTNER: 'PARTNER',
  /**
   * #965 (2026-10-13 19:09 +0200) — licença comprada pelo parceiro `RESELLER`
   * a preço fixo (`pricingFloors.partner_license`), cobrada do parceiro
   * (`Subscription.bill_to`). Descritivo como os outros: quem decide o pagador
   * é o `bill_to`, nunca este campo.
   */
  PARTNER_LICENSE: 'PARTNER_LICENSE',
  /** #942 (2026-10-12 11:56 +0200) — preço da App Store (IAP self-service no iOS). */
  APP_STORE: 'APP_STORE',
  /** #969 (2026-10-14 13:31 +0200) — preço do Google Play (self-service no Android). */
  GOOGLE_PLAY: 'GOOGLE_PLAY',
  /**
   * #969 (2026-10-14 13:31 +0200) — preço self-service cobrado pela Stripe (web e
   * alternativa do User Choice Billing no Android), tirado do Price ativo da
   * zona na Stripe (`SELF_SERVICE_STRIPE_PRICE_LOOKUP_KEY`, #970) no checkout.
   */
  SELF_SERVICE: 'SELF_SERVICE',
} as const;
export type PricingSource = (typeof PricingSource)[keyof typeof PricingSource];
export const ALL_PRICING_SOURCES = Object.values(PricingSource) as [
  PricingSource,
  ...PricingSource[],
];

/**
 * De onde vem um item de `Subscription.items[]`: vendido pelo nosso gateway
 * (`GATEWAY`), concedido sem cobrança (`GRANT`) ou vendido pelo parceiro, que
 * cobra o cliente e nos repassa (`PARTNER`, com `remit_cents`).
 *
 * Origem: #761 — 2026-09-27 17:09 +0200. O campo nasceu com `GRANT` na #699
 * (`c2714b109`, 2026-09-22) e ganhou `PARTNER` na #743 (`568ac8f96`,
 * 2026-09-25).
 *
 * Por quê: a lista existia em TRÊS lugares — o `z.enum` de
 * `SubscriptionItemSchema.origin` e as uniões redigitadas de
 * `SubscriptionItemGatewayFields` (`billing.ts`) e `PostSaleItemFields`
 * (`postSale.ts`). Um valor novo em um só deles compilaria. E `'PARTNER'`
 * aqui não é o modo do tenant nem o papel do usuário: a constante diz qual.
 *
 * Contrato: ausente = `GATEWAY` na leitura (item legado); o schema aceita o
 * campo opcional. Valores gravados não mudam.
 *
 * Usado por: `SubscriptionItemSchema` (`schemas.ts`), `billing.ts`,
 * `postSale.ts`, `assistedSale.ts`, `partnerRemittance.ts`,
 * `tenantProvisioning.ts`; Functions (concessão de módulo, transição de modo,
 * pós-venda do parceiro, venda do Asaas), CRM Functions (repasse, vínculo de
 * pagamento) e CRM/admin web.
 *
 * Issues: #761, #699 (item concedido), #743 (item de parceiro).
 */
export const SubscriptionItemOrigin = {
  GATEWAY: 'GATEWAY',
  GRANT: 'GRANT',
  PARTNER: 'PARTNER',
  /**
   * #942 (2026-10-12 11:56 +0200) — item incluído no plano `ALL_IN_ONE` da App Store. Sem
   * contrapartida no nosso gateway (ids externos nulos, `amount_cents: 0`): a
   * Apple cobra o plano inteiro. A reconciliação diária mantém um item por
   * SKU vendável de `/config/addons`.
   * #969 (2026-10-14 13:31 +0200) — vale também para o `ALL_IN_ONE` do Google
   * Play e da Stripe self-service: o nome ficou "IAP" por história, o sentido é
   * "módulo incluso no plano all-in-one". Na Stripe o gateway cobra UM price
   * (o do plano), e os itens locais sem id externo atravessam os resets do
   * webhook como os concedidos (`extractGrantedItems`).
   */
  IAP_BUNDLE: 'IAP_BUNDLE',
} as const;
export type SubscriptionItemOrigin = (typeof SubscriptionItemOrigin)[keyof typeof SubscriptionItemOrigin];
export const ALL_SUBSCRIPTION_ITEM_ORIGINS = Object.values(SubscriptionItemOrigin) as [
  SubscriptionItemOrigin,
  ...SubscriptionItemOrigin[],
];

/**
 * #679 §4.2 — quem gerou uma Checkout Session: o próprio cliente no app
 * (`CLIENT`, o self-service que a F4 remove), o super-admin (`ADMIN`,
 * `adminCreateCheckoutLink`, #289) ou o colaborador do CRM na venda assistida
 * (`CRM_SALE`). Entra na chave de reuso da sessão: uma sessão aberta pelo
 * cliente não pode ser devolvida como link de venda, nem o contrário.
 */
export const CheckoutOrigin = {
  CLIENT: 'CLIENT',
  ADMIN: 'ADMIN',
  CRM_SALE: 'CRM_SALE',
} as const;
export type CheckoutOrigin = (typeof CheckoutOrigin)[keyof typeof CheckoutOrigin];
export const ALL_CHECKOUT_ORIGINS = Object.values(CheckoutOrigin) as [
  CheckoutOrigin,
  ...CheckoutOrigin[],
];

/**
 * Eventos de conversão de mídia paga enviados ao Meta Conversions API e ao
 * GA4 Measurement Protocol (`dispatchAdsConversion`). Nomes canônicos do
 * EtiquetaChef — a tradução para o `event_name` de cada rede acontece no
 * `sendMetaCapi` / `sendGa4Mp`. O `event_id` compartilhado com o Pixel web
 * garante dedup ~100% (ver `docs/ads-event-id.md`).
 *
 * Origem: #93 — 2026-06-27 15:38 +0200.
 *
 * Usado por: `apps/functions/src/ads/**` (Functions) — nenhum cliente.
 *
 * Histórico:
 *  - 2026-10-15 12:07 +0200 — (#972) `trial_started` e
 *    `self_service_subscribed`: o self-service (#969) tem trial de 7 dias, e
 *    o texto antigo ("NÃO há `trial_started`: o produto não tem trial")
 *    deixou de ser verdade. `trial_started` = assinatura self-service com
 *    trial (dia 0); `self_service_subscribed` = estabelecimento adicional
 *    (#970), que é pago no dia 0, sem trial.
 */
export const AdsConversionEvent = {
  SIGNUP_COMPLETED: 'signup_completed',
  CHECKOUT_COMPLETED: 'checkout_completed',
  SUBSCRIPTION_RENEWED: 'subscription_renewed',
  TRIAL_STARTED: 'trial_started',
  SELF_SERVICE_SUBSCRIBED: 'self_service_subscribed',
} as const;
export type AdsConversionEvent = (typeof AdsConversionEvent)[keyof typeof AdsConversionEvent];
export const ALL_ADS_CONVERSION_EVENTS = Object.values(AdsConversionEvent) as [
  AdsConversionEvent,
  ...AdsConversionEvent[],
];

/**
 * Segmentação cliente novo vs. existente no `custom_data` do Meta CAPI
 * (`customer_segmentation`). Regra: tem pagamento confirmado anterior →
 * `EXISTING`; senão `NEW`. Signup é sempre `NEW`; renovação sempre `EXISTING`.
 * Strings exatas exigidas pelo Meta.
 */
export const AdsCustomerSegmentation = {
  NEW: 'new_customer_to_business',
  EXISTING: 'existing_customer_to_business',
} as const;
export type AdsCustomerSegmentation =
  (typeof AdsCustomerSegmentation)[keyof typeof AdsCustomerSegmentation];
export const ALL_ADS_CUSTOMER_SEGMENTATIONS = Object.values(
  AdsCustomerSegmentation,
) as [AdsCustomerSegmentation, ...AdsCustomerSegmentation[]];

/**
 * Plataforma em que a conversão aconteceu — decide o `action_source` da Meta
 * (`website` × `app`, com `app_data`) e o stream do GA4 (web × Firebase
 * Android/iOS).
 *
 * Origem: #972 — 2026-10-15 12:07 +0200.
 *
 * Usado por: `apps/functions/src/ads/**` (Functions). Ausente na conversão =
 * o comportamento anterior à #972 (fonte fixa por evento).
 */
export const AdsConversionChannel = {
  WEB: 'web',
  ANDROID: 'android',
  IOS: 'ios',
} as const;
export type AdsConversionChannel =
  (typeof AdsConversionChannel)[keyof typeof AdsConversionChannel];
export const ALL_ADS_CONVERSION_CHANNELS = Object.values(AdsConversionChannel) as [
  AdsConversionChannel,
  ...AdsConversionChannel[],
];

/**
 * Status da assinatura — máquina de estados gerenciada pelo
 * `dailyBillingTick` e callables admin (`suspendTenant`/`reactivateTenant`).
 *
 * - `ACTIVE`: criada no signup; em dia.
 * - `PAST_DUE`: invoice venceu sem pagamento; ainda pode usar (grace).
 * - `SUSPENDED`: dias atrasados >= `suspend_after_days_overdue`; perde acesso.
 * - `CANCELLED`: terminal — reativar exige nova assinatura.
 */
export const SubscriptionStatus = {
  ACTIVE: 'ACTIVE',
  PAST_DUE: 'PAST_DUE',
  SUSPENDED: 'SUSPENDED',
  CANCELLED: 'CANCELLED',
} as const;
export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];
export const ALL_SUBSCRIPTION_STATUSES = Object.values(SubscriptionStatus) as [
  SubscriptionStatus,
  ...SubscriptionStatus[],
];

/**
 * Estado de uma tentativa de checkout (`/checkoutAttempts/{sessionId}`).
 *
 * Existe porque falha ANTES da assinatura ativar era invisível: sem invoice,
 * o `invoice.payment_failed` nunca dispara, e a Checkout Session morria em 24h
 * sem deixar rastro (#289). Não confundir com `SubscriptionStatus` — uma
 * tentativa não é uma assinatura, e de propósito não vive no doc
 * `billing/subscription` (o tenant nasce sem subscription).
 *
 * - `OPEN`      — sessão criada, cliente ainda pode pagar.
 * - `EXPIRED`   — sessão passou do `expires_at` sem pagamento; se
 *                 `recovery_url` estiver preenchida, ainda dá para recuperar.
 * - `FAILED`    — o meio de pagamento falhou (Pix expirado, cartão recusado)
 *                 mas a sessão pode continuar aberta para nova tentativa.
 * - `COMPLETED` — pago; a assinatura foi materializada.
 */
export const CheckoutAttemptStatus = {
  OPEN: 'OPEN',
  EXPIRED: 'EXPIRED',
  FAILED: 'FAILED',
  COMPLETED: 'COMPLETED',
} as const;
export type CheckoutAttemptStatus =
  (typeof CheckoutAttemptStatus)[keyof typeof CheckoutAttemptStatus];
export const ALL_CHECKOUT_ATTEMPT_STATUSES = Object.values(
  CheckoutAttemptStatus,
) as [CheckoutAttemptStatus, ...CheckoutAttemptStatus[]];

/**
 * Status da fatura. `OVERDUE` é set pelo `dailyBillingTick` quando
 * `due_at < now && status === 'PENDING'`. `REFUNDED` só é set na
 * Sprint 5 (gateway); aqui o estado existe mas sem trigger. `VOID` é
 * set pelo helper `cancelSubscription` (Sprint 19) quando o tenant é
 * encerrado por super-admin: faturas em aberto (PENDING/OVERDUE) são
 * marcadas como `VOID` com `void_reason: 'tenant_deleted'` e deixam
 * de cobrar.
 */
export const InvoiceStatus = {
  PENDING: 'PENDING',
  PAID: 'PAID',
  OVERDUE: 'OVERDUE',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
  VOID: 'VOID',
} as const;
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];
export const ALL_INVOICE_STATUSES = Object.values(InvoiceStatus) as [InvoiceStatus, ...InvoiceStatus[]];

/**
 * Status do pagamento. Sprint 16 cria pagamento direto em `CONFIRMED`
 * via `recordManualPayment` (super-admin marca fatura como paga). Os
 * demais estados (`PROCESSING`, `FAILED`, `REFUNDED`,
 * `PARTIALLY_REFUNDED`) só vão ser usados na Sprint 5 com gateway.
 */
export const PaymentStatus = {
  CONFIRMED: 'CONFIRMED',
  FAILED: 'FAILED',
  PROCESSING: 'PROCESSING',
  REFUNDED: 'REFUNDED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];
export const ALL_PAYMENT_STATUSES = Object.values(PaymentStatus) as [PaymentStatus, ...PaymentStatus[]];

/**
 * Tipo de método de pagamento. `manual` cobre Sprint 16 (super-admin
 * marca como paga); demais tipos são placeholders que a Sprint 5
 * preenche via gateway (Stripe).
 */
export const PaymentMethodType = {
  MANUAL: 'manual',
  CARD: 'card',
  PIX: 'pix',
  BOLETO: 'boleto',
  SEPA_DEBIT: 'sepa_debit',
  // Link = acelerador de cartão do Stripe (carteira). Aparece no Checkout só
  // onde mandamos 'link' nos payment_methods do mercado (USD: US/AR/MX/CL/PY).
  // No BR fica fora pra não competir com o Pix. Não é método salvo no cliente
  // (pagamento via Link grava como `card`); existe aqui pro catálogo de métodos
  // do mercado.
  LINK: 'link',
} as const;
export type PaymentMethodType = (typeof PaymentMethodType)[keyof typeof PaymentMethodType];
export const ALL_PAYMENT_METHOD_TYPES = Object.values(PaymentMethodType) as [
  PaymentMethodType,
  ...PaymentMethodType[],
];

/**
 * Status do método de pagamento cadastrado pelo cliente. Sprint 5
 * preenche via webhook do gateway.
 */
export const PaymentMethodStatus = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  INVALID: 'INVALID',
} as const;
export type PaymentMethodStatus = (typeof PaymentMethodStatus)[keyof typeof PaymentMethodStatus];
export const ALL_PAYMENT_METHOD_STATUSES = Object.values(PaymentMethodStatus) as [
  PaymentMethodStatus,
  ...PaymentMethodStatus[],
];

/**
 * Provider de pagamento. A lista fechada de 4 PSPs (owner 2026-05-25) foi
 * revogada em 2026-09-26 (#748): **ASAAS é o gateway padrão do BR** (venda
 * direta e de parceiro, com split e NFS-e próprios) e **STRIPE** (PJ
 * italiana) é o padrão da Europa e opção por venda no BR. O gateway de uma
 * venda é escolhido na Contratação entre os `allowed_payment_providers` do
 * mercado e fica gravado na assinatura (`external_provider`) — cada tenant
 * segue no gateway em que foi vendido. PADDLE/DLOCAL/EBANX seguem sem
 * implementação. `manual` cobre pagamentos via super-admin
 * (`recordManualPayment`) — não é PSP, é registro local. `mercadopago` fica
 * até cleanup: foi avaliado e descartado na #748 (no split dele quem recebe o
 * bruto é o vendedor, e não há split em assinatura).
 */
export const PaymentProvider = {
  MANUAL: 'manual',
  STRIPE: 'stripe',
  PADDLE: 'paddle',
  DLOCAL: 'dlocal',
  EBANX: 'ebanx',
  /**
   * #748 — gateway padrão do BR (já existia no enum desde 2026-05-04 23:12
   * +0200, sem implementação; ganhou uso real na A1, #750, 2026-09-27 12:00
   * +0200). Ids `cus_`/`sub_`/`pay_` colidem com o
   * formato do Stripe, por isso todo vínculo do webhook filtra também por
   * `external_provider: asaas` e `livemode` (docs/billing/asaas.md, "Contrato
   * de identidade"). Venda pela `/pay` desde a A3 (#752); modo live só na A7
   * (#756) — até lá, só o sandbox do tenant de teste.
   *
   * Usado por: o `external_provider` do `billing/subscription` e o `provider`
   * de `/checkoutAttempts` (Functions); `saleGateway.ts`; a Contratação do CRM
   * (seletor de gateway); o contrato gerado do iOS (`PaymentProvider` em
   * `Enums.swift`, com `readFallback: null`).
   */
  ASAAS: 'asaas',
  MERCADOPAGO: 'mercadopago',
  /**
   * #942 (2026-10-12 11:56 +0200) — App Store (In-App Purchase do iOS). A Apple cobra, renova e
   * expira sozinha; nós só espelhamos pelo App Store Server Notifications V2 e
   * pela reconciliação diária. `external_subscription_id = aps_<originalTransactionId>`.
   */
  APPSTORE: 'appstore',
  /**
   * #969 (2026-10-14 13:31 +0200) — Google Play Billing (assinatura self-service no Android).
   * Como a App Store: o Google cobra, renova e expira sozinho; nós espelhamos
   * pelas Real-time Developer Notifications (Pub/Sub) e pela reconciliação
   * diária. `external_subscription_id = gps_<sha256(purchaseToken) hex[0..40]>`
   * — o purchaseToken tem pontos e hífens e não cabe no formato de id externo.
   */
  GOOGLEPLAY: 'googleplay',
} as const;
export type PaymentProvider = (typeof PaymentProvider)[keyof typeof PaymentProvider];
export const ALL_PAYMENT_PROVIDERS = Object.values(PaymentProvider) as [
  PaymentProvider,
  ...PaymentProvider[],
];

/**
 * Modo em que o PSP ativo de um mercado cobra: `live` (dinheiro de verdade) ou
 * `sandbox` (ensaio).
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7 do Asaas: o admin escolhe por país
 * STRIPE / STRIPE SANDBOX / ASAAS / ASAAS SANDBOX).
 *
 * Por quê: o owner quer trocar o gateway de um país, e ensaiá-lo, pelo admin e
 * sem deploy. O modo mora no mercado (`MarketConfig.payment_mode`), não na
 * chave: as duas chaves de cada PSP ficam carregadas em toda Function que vende.
 *
 * Contrato: `sandbox` é ENSAIO — só os tenants da allowlist de teste
 * (`TEST_MODE_BILLING_TENANT_IDS`) vendem, e em sandbox; cliente real é
 * recusado com `market_in_sandbox` (owner, 2026-10-01). Ausente no doc = `live`.
 *
 * Usado por: `MarketConfigSchema.payment_mode`; Functions — `setMarketConfig`,
 * `lib/billing/saleGateway.ts`, `billing/checkoutSessionCore.ts`; admin —
 * `MarketConfigEditDialog`.
 *
 * Arquitetura: a proteção da #258 continua valendo — webhook sandbox de tenant
 * fora da allowlist é descartado (`resolveTenantForWrite`). Por isso o sandbox
 * nunca pode vender para cliente real: o pagamento seria ignorado.
 */
export const PaymentMode = {
  LIVE: 'live',
  SANDBOX: 'sandbox',
} as const;
export type PaymentMode = (typeof PaymentMode)[keyof typeof PaymentMode];
export const ALL_PAYMENT_MODES = Object.values(PaymentMode) as [PaymentMode, ...PaymentMode[]];

/**
 * Sprint 5 — status de um refund. `PENDING` enquanto o gateway processa,
 * `SUCCEEDED` quando o estorno foi confirmado (chega via webhook
 * `charge.refunded` no Stripe), `FAILED` se o provider rejeitou
 * (ex: charge muito antigo ou já estornado).
 */
export const RefundStatus = {
  PENDING: 'PENDING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
} as const;
export type RefundStatus = (typeof RefundStatus)[keyof typeof RefundStatus];
export const ALL_REFUND_STATUSES = Object.values(RefundStatus) as [RefundStatus, ...RefundStatus[]];

/**
 * Sprint 5 — origem do refund. `MBG` é o money-back guarantee automático
 * disparado quando o tenant é encerrado dentro da janela (7d mensal /
 * 14d anual). `REQUESTED_BY_SUPPORT` é refund manual via super-admin.
 * `DISPUTE` é chargeback do cartão. `DUPLICATE`/`FRAUDULENT` são códigos
 * Stripe padrão. `COMMERCIAL` é cortesia/compensação comercial; `OTHER` é
 * qualquer outro motivo manual (detalhar nas notas).
 */
export const RefundReason = {
  MBG: 'MBG',
  REQUESTED_BY_SUPPORT: 'REQUESTED_BY_SUPPORT',
  DUPLICATE: 'DUPLICATE',
  FRAUDULENT: 'FRAUDULENT',
  DISPUTE: 'DISPUTE',
  COMMERCIAL: 'COMMERCIAL',
  OTHER: 'OTHER',
} as const;
export type RefundReason = (typeof RefundReason)[keyof typeof RefundReason];
export const ALL_REFUND_REASONS = Object.values(RefundReason) as [RefundReason, ...RefundReason[]];

/**
 * Razões de suspensão do tenant. Gerada pelo scheduler ou manual via
 * super-admin.
 */
export const SuspensionReason = {
  BILLING_OVERDUE: 'BILLING_OVERDUE',
  MANUAL: 'MANUAL',
  POLICY_VIOLATION: 'POLICY_VIOLATION',
} as const;
export type SuspensionReason = (typeof SuspensionReason)[keyof typeof SuspensionReason];
export const ALL_SUSPENSION_REASONS = Object.values(SuspensionReason) as [
  SuspensionReason,
  ...SuspensionReason[],
];

/**
 * Moeda canônica de cada país. Sprint 35 fechou em **3 moedas**
 * (BRL/EUR/USD — decisão owner 2026-05-25, memória `project_currencies_locked_3`).
 * Listamos apenas BR + UE explicitamente; **USD é fallback default** para
 * qualquer outro país via `currencyForCountry()`. Países UE bloqueados
 * para venda no MVP (`saleable: false` em `/config/markets/{country}`)
 * mas a moeda já está mapeada para futuro habilitar via Paddle.
 *
 * Tipo `Record<string, Currency>` (não `Record<Country, Currency>`) porque
 * `country` agora é ISO-3166 alpha-2 livre — habilitar país novo é
 * configuração em runtime (`/config/markets/{code}`), nunca expansão de
 * enum + release de app. Helper canônico: `currencyForCountry(code)`.
 */
export const CURRENCY_BY_COUNTRY: Readonly<Record<string, Currency>> = {
  // BRL (único país)
  BR: Currency.BRL,
  // EUR — 27 estados-membros da UE
  IT: Currency.EUR, DE: Currency.EUR, FR: Currency.EUR, ES: Currency.EUR,
  PT: Currency.EUR, NL: Currency.EUR, BE: Currency.EUR, AT: Currency.EUR,
  IE: Currency.EUR, FI: Currency.EUR, SE: Currency.EUR, DK: Currency.EUR,
  PL: Currency.EUR, GR: Currency.EUR, HU: Currency.EUR, CZ: Currency.EUR,
  RO: Currency.EUR, BG: Currency.EUR, SK: Currency.EUR, SI: Currency.EUR,
  HR: Currency.EUR, EE: Currency.EUR, LV: Currency.EUR, LT: Currency.EUR,
  LU: Currency.EUR, MT: Currency.EUR, CY: Currency.EUR,
};

/**
 * Resolve a moeda de um país a partir do código ISO-3166 alpha-2.
 * Países UE → EUR; BR → BRL; **qualquer outro → USD** (fallback default).
 * Não adicionar ARS/MXN/CLP/PYG: produto opera com apenas 3 moedas
 * (memória `project_currencies_locked_3`).
 */
export function currencyForCountry(countryCode: string): Currency {
  return CURRENCY_BY_COUNTRY[countryCode] ?? Currency.USD;
}

/**
 * Estados-membros da UE (ISO-3166 alpha-2). Usado pelos Guards 6/7/8 de
 * roteamento de PSP/fiscal (UE locked-to-Paddle; IT exige Paddle MoR; não-UE
 * não pode usar Paddle — memórias `project_psps_locked_4` +
 * `project_italian_entity`). Imutável — Brexit consolidou em 27.
 */
export const EU_COUNTRIES: readonly string[] = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
  'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
  'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
];

/**
 * Helper para Guards 6/7/8 e UI: país está sob jurisdição UE?
 * Aceita ISO-3166 alpha-2 livre — não exige Country enum (que é o
 * subset saleable do MVP).
 */
export function isEuCountry(countryCode: string): boolean {
  return EU_COUNTRIES.includes(countryCode);
}

/**
 * Ações do log de auditoria `/audit/{eventId}`. Registradas por callables
 * admin (`suspendTenant`, `setTenantPlan`, `recordPayment`, etc.) e por
 * eventos privilegiados (bootstrap super-admin). Acrescentar novas
 * variantes conforme novas operações admin forem criadas — Sprint 2+.
 */
export const AuditAction = {
  SUPER_ADMIN_BOOTSTRAPPED: 'SUPER_ADMIN_BOOTSTRAPPED',
  SUPER_ADMIN_GRANTED: 'SUPER_ADMIN_GRANTED',
  SUPER_ADMIN_REVOKED: 'SUPER_ADMIN_REVOKED',
  // Sprint 11 — sub-roles granulares de admin.
  ADMIN_ROLE_GRANTED: 'ADMIN_ROLE_GRANTED',
  ADMIN_ROLE_REVOKED: 'ADMIN_ROLE_REVOKED',
  ADMIN_ROLE_CHANGED: 'ADMIN_ROLE_CHANGED',
  TENANT_SUSPENDED: 'TENANT_SUSPENDED',
  TENANT_REACTIVATED: 'TENANT_REACTIVATED',
  // Sprint 19 — soft-delete reversível de tenant pelo super-admin.
  TENANT_DELETED: 'TENANT_DELETED',
  TENANT_RESTORED: 'TENANT_RESTORED',
  // Marca/desmarca o tenant como demonstração comercial (`Tenant.isDemo`),
  // via `setTenantDemoFlag`. Auditado porque a flag esconde o tenant de
  // relatórios internos — precisa haver rastro de quem escondeu.
  TENANT_DEMO_FLAG_SET: 'TENANT_DEMO_FLAG_SET',
  // Sprint 13 — LGPD/GDPR data subject rights.
  USER_DATA_EXPORTED: 'USER_DATA_EXPORTED',
  USER_DELETION_REQUESTED: 'USER_DELETION_REQUESTED',
  USER_DELETION_RESTORED: 'USER_DELETION_RESTORED',
  USER_HARD_DELETED: 'USER_HARD_DELETED',
  TENANT_HARD_DELETED: 'TENANT_HARD_DELETED',
  TENANT_DELETION_CONFIRMATION_REQUESTED: 'TENANT_DELETION_CONFIRMATION_REQUESTED',
  TENANT_DELETION_SELF_SERVICE_CONFIRMED: 'TENANT_DELETION_SELF_SERVICE_CONFIRMED',
  PLAN_ACTIVATED: 'PLAN_ACTIVATED',
  // #679 F1 — tenant criado pela venda assistida (`provisionTenantForSale`),
  // pelo colaborador do CRM. `targetType = TENANT`; `after` carrega a
  // composição negociada e o retrato do piso vigente (9.1).
  TENANT_PROVISIONED_FOR_SALE: 'TENANT_PROVISIONED_FOR_SALE',
  // #942 (2026-10-12 12:10 +0200) — tenant criado pela assinatura self-service
  // da App Store (`activateAppStorePurchase` ou ASSN `SUBSCRIBED`).
  // `targetType = TENANT`; `after` carrega a transação Apple e os módulos.
  TENANT_PROVISIONED_SELF_SERVICE: 'TENANT_PROVISIONED_SELF_SERVICE',
  // Sprint 16
  INVOICE_CREATED: 'INVOICE_CREATED',
  INVOICE_PAID: 'INVOICE_PAID',
  INVOICE_CANCELLED: 'INVOICE_CANCELLED',
  PAYMENT_RECORDED: 'PAYMENT_RECORDED',
  BILLING_SETTINGS_UPDATED: 'BILLING_SETTINGS_UPDATED',
  PLAN_PRICE_UPDATED: 'PLAN_PRICE_UPDATED',
  PAYMENT_METHOD_ADDED: 'PAYMENT_METHOD_ADDED',
  PAYMENT_METHOD_REMOVED: 'PAYMENT_METHOD_REMOVED',
  PAYMENT_METHOD_DEFAULT_CHANGED: 'PAYMENT_METHOD_DEFAULT_CHANGED',
  BILLING_CYCLE_CHANGED: 'BILLING_CYCLE_CHANGED',
  // Sprint 21 — fuso do tenant alterado pelo OWNER (geo ou dropdown).
  TENANT_TIMEZONE_UPDATED: 'TENANT_TIMEZONE_UPDATED',
  // Sprint 22+ — nome e/ou endereço do estabelecimento editados pelo OWNER
  // em /settings → Estabelecimento (web e Android).
  TENANT_BASICS_UPDATED: 'TENANT_BASICS_UPDATED',
  // Bug-hunt 2026-05-08: ações dedicadas para evitar reutilizar
  // SUPER_ADMIN_BOOTSTRAPPED em drilldown de BI (A-14) e
  // BILLING_SETTINGS_UPDATED em cancelamento de subscription (M-27).
  // Sem isso, dashboards filtravam por action e operações
  // corriqueiras apareciam como eventos críticos.
  BI_DRILLDOWN: 'BI_DRILLDOWN',
  // Sprint 4.x — recompute manual de agregações (backfill super-admin via
  // `backfillAggregations`). Ação dedicada para não confundir um recompute
  // de série temporal com a navegação de drilldown (BI_DRILLDOWN).
  BI_BACKFILL_TRIGGERED: 'BI_BACKFILL_TRIGGERED',
  // Épico #323 fase F2 (#331) — execução da migração das etiquetas para o
  // schema v2 (`backfillLabelBaixaQuantities`). `targetType = SYSTEM`,
  // `targetId` = o `migrationRunId`, que é a chave para rastrear qualquer
  // divergência histórica até a execução que a produziu.
  LABEL_SCHEMA_BACKFILL_RUN: 'LABEL_SCHEMA_BACKFILL_RUN',
  BILLING_SUBSCRIPTION_CANCELLED: 'BILLING_SUBSCRIPTION_CANCELLED',
  // Prorrogação manual do próximo vencimento (super-admin): empurra o
  // `current_period_end` no PSP via trial_end, sem cobrar o intervalo.
  BILLING_SUBSCRIPTION_RENEWAL_POSTPONED: 'BILLING_SUBSCRIPTION_RENEWAL_POSTPONED',
  // Notificações administrativas — broadcast manual super-admin para
  // todos os OWNERs (multicast desde a #767).
  BROADCAST_OWNERS_SENT: 'BROADCAST_OWNERS_SENT',
  // Legado (#767): gravado pelo backfill `syncOwnersTopic`, removido junto
  // com o tópico FCM. Nada grava mais este valor; ele fica porque há eventos
  // antigos em `/audit` com ele e o filtro do admin precisa reconhecê-los.
  OWNERS_TOPIC_SYNCED: 'OWNERS_TOPIC_SYNCED',
  // Sprint 38 PR 38-E — revoke nuclear da sessão de acesso rápido do
  // STAFF (`docs/screens/staff-unlock.md` §6). Disparado pelo callable
  // `revokeStaffDeviceSession`: invalida TODOS os refresh tokens do
  // uid-alvo, em todos os devices (não apenas o que o STAFF está
  // usando). `targetType = USER`, `targetId = uid revogado`.
  STAFF_SESSION_REVOKED: 'STAFF_SESSION_REVOKED',
  // Issue #214 — o próprio titular encerra todas as suas sessões pela tela
  // `/account/sessions`. Difere de `STAFF_SESSION_REVOKED`, que é um terceiro
  // (OWNER/super-admin) revogando a sessão de outra pessoa. `targetType =
  // USER`, `targetId = o próprio uid`.
  USER_SESSIONS_REVOKED: 'USER_SESSIONS_REVOKED',
  // Issue #257 — tela global de Usuários no admin. O super-admin carimba ou
  // revoga a verificação de contato de um usuário, contornando o OTP/link.
  // Escrevem SEMPRE as duas fontes juntas (Firebase Auth + espelho em
  // `/profiles`), porque o gate de e-mail lê o Auth e o de telefone lê o
  // profile — mexer só numa das pontas foi o que produziu o bug da issue.
  // `targetType = USER`, `targetId = uid alvo`.
  USER_EMAIL_VERIFICATION_SET: 'USER_EMAIL_VERIFICATION_SET',
  USER_PHONE_VERIFICATION_SET: 'USER_PHONE_VERIFICATION_SET',
  // #746 — convite de membro ou venda assistida encontrou conta Auth com o
  // e-mail da pessoa mas SEM e-mail verificado: qualquer um pode tê-la criado
  // pelo cadastro público. A conta é neutralizada (senha aleatória + refresh
  // tokens revogados) antes de ser vinculada, e a pessoa define a senha pelo
  // link do e-mail. `targetType = USER`, `targetId = uid neutralizado`.
  UNVERIFIED_ACCOUNT_CLAIMED: 'UNVERIFIED_ACCOUNT_CLAIMED',
  // Bloqueio/desbloqueio da conta no Firebase Auth (`disabled`) pelo
  // super-admin. Reversível e sem apagar dado — não confundir com os
  // `USER_DELETION_*`/`USER_HARD_DELETED` do fluxo LGPD.
  USER_ACCOUNT_DISABLED: 'USER_ACCOUNT_DISABLED',
  USER_ACCOUNT_ENABLED: 'USER_ACCOUNT_ENABLED',
  // A própria pessoa desliga o acesso pelo app (callable
  // `deactivateMyAccount`). Ação própria, e não `USER_ACCOUNT_DISABLED`, porque
  // é a PROVENIÊNCIA que decide quem pode religar: o dono do estabelecimento
  // desfaz o que a pessoa fez em si mesma, e nunca um bloqueio do suporte.
  // Nada é apagado — memberships e dados ficam de pé.
  USER_ACCOUNT_SELF_DEACTIVATED: 'USER_ACCOUNT_SELF_DEACTIVATED',
  // O dono do estabelecimento religa o acesso de um membro que havia se
  // desativado. Só alcança `accessDisabledBy = 'SELF'`; bloqueio do suporte
  // continua sendo `USER_ACCOUNT_ENABLED`, feito pelo painel.
  USER_ACCOUNT_REACTIVATED_BY_TENANT_OWNER: 'USER_ACCOUNT_REACTIVATED_BY_TENANT_OWNER',
  // Super-admin derruba todas as sessões de um terceiro. Difere de
  // `USER_SESSIONS_REVOKED` (o próprio titular, em `/account/sessions`) e de
  // `STAFF_SESSION_REVOKED` (OWNER revogando acesso rápido de STAFF).
  ADMIN_USER_SESSIONS_REVOKED: 'ADMIN_USER_SESSIONS_REVOKED',
  // Issue #263 — integrações da API pública readonly. `targetType = API_KEY`,
  // `targetId = keyId`. `API_KEY_REVOKED` é irreversível e mata na hora todos
  // os access tokens em circulação daquela integração (a checagem de `active`
  // roda em todo request), por isso é ação própria e não um `UPDATED`.
  API_KEY_CREATED: 'API_KEY_CREATED',
  API_KEY_UPDATED: 'API_KEY_UPDATED',
  API_KEY_REVOKED: 'API_KEY_REVOKED',
  // Rotação do `client_secret`: a integração continua a mesma (o `clientId`
  // não muda), mas o segredo anterior morre no ato. Ação própria porque o
  // efeito é derrubar o parceiro até ele trocar a credencial — não é um
  // `UPDATED` qualquer.
  API_KEY_SECRET_ROTATED: 'API_KEY_SECRET_ROTATED',
  // #791 — remoção definitiva de uma integração JÁ revogada. O doc sai de
  // `/api_keys`, e este evento (com nome, clientId e alcance no `before`)
  // passa a ser o histórico dela, junto do `API_KEY_REVOKED` anterior.
  API_KEY_DELETED: 'API_KEY_DELETED',
  // #792 — assinaturas de webhook geridas pelo PARCEIRO no portal de
  // desenvolvedores. `targetType = API_KEY`, `targetId = keyId`; o `after`
  // leva `subscriptionId`, URL e eventos. O ator é a conta Google do portal
  // (`actorEmail`), não um usuário do app — `actorUserId` é o uid do Auth.
  WEBHOOK_SUBSCRIPTION_CREATED: 'WEBHOOK_SUBSCRIPTION_CREATED',
  WEBHOOK_SUBSCRIPTION_UPDATED: 'WEBHOOK_SUBSCRIPTION_UPDATED',
  WEBHOOK_SUBSCRIPTION_DELETED: 'WEBHOOK_SUBSCRIPTION_DELETED',
  // Rotação do segredo de assinatura: própria, como `API_KEY_SECRET_ROTATED`,
  // porque o parceiro que não trocar o segredo em 24 h para de verificar.
  WEBHOOK_SECRET_ROTATED: 'WEBHOOK_SECRET_ROTATED',
  // #803 — webhooks de ENTRADA (o parceiro envia, nós gravamos).
  INBOUND_WEBHOOK_ENDPOINT_CREATED: 'INBOUND_WEBHOOK_ENDPOINT_CREATED',
  INBOUND_WEBHOOK_ENDPOINT_UPDATED: 'INBOUND_WEBHOOK_ENDPOINT_UPDATED',
  INBOUND_WEBHOOK_ENDPOINT_DELETED: 'INBOUND_WEBHOOK_ENDPOINT_DELETED',
  INBOUND_WEBHOOK_SECRET_ROTATED: 'INBOUND_WEBHOOK_SECRET_ROTATED',
  // #655 — aperto do kill switch de versão (`/appConfig/{platform}.minBuild`).
  // Trava todos os aparelhos de uma plataforma abaixo daquele build, e a tela
  // de bloqueio não tem saída por desenho: é a ação de maior alcance do painel,
  // e a única de que se precisa saber o motivo meses depois.
  APP_VERSION_GATE_UPDATED: 'APP_VERSION_GATE_UPDATED',
  // #679 9.13 — troca da versão vigente dos Termos (`/appConfig/terms`). Muda
  // qual aceite vale: é o registro de QUANDO cada versão passou a valer.
  TERMS_VERSION_UPDATED: 'TERMS_VERSION_UPDATED',
  // #1078 (F2 da #960) — chave `config/regions.europeWest1Enabled`: decide se tenants novos fora da
  // América do Sul passam a nascer na Bélgica. Decisão de plataforma (ROOT), com motivo.
  REGIONS_CONFIG_UPDATED: 'REGIONS_CONFIG_UPDATED',
  // #795 (Admin, 2026-10-08) — razão financeiro interno (`/finance_*`, #73-#75).
  // Antes nenhuma mutação deixava rastro: um lançamento apagado ou com valor
  // trocado não dizia quem, quando nem o que havia. Gravados no MESMO batch da
  // mutação (`apps/functions/src/finance/financeAudit.ts`).
  FINANCE_ACCOUNT_CREATED: 'FINANCE_ACCOUNT_CREATED',
  FINANCE_ACCOUNT_UPDATED: 'FINANCE_ACCOUNT_UPDATED',
  FINANCE_ACCOUNT_ARCHIVED: 'FINANCE_ACCOUNT_ARCHIVED',
  FINANCE_ACCOUNT_UNARCHIVED: 'FINANCE_ACCOUNT_UNARCHIVED',
  FINANCE_ENTRY_CREATED: 'FINANCE_ENTRY_CREATED',
  FINANCE_ENTRY_UPDATED: 'FINANCE_ENTRY_UPDATED',
  // O doc sai de `/finance_entries`; o `before` deste evento é o único histórico.
  FINANCE_ENTRY_DELETED: 'FINANCE_ENTRY_DELETED',
  // Um evento por import confirmado (não por linha): `after` leva contagens e
  // total por tipo; as linhas têm `importId` e se acham por ele.
  FINANCE_IMPORT_CONFIRMED: 'FINANCE_IMPORT_CONFIRMED',
  // #937 (2026-10-15) — `migrateStaffEmails` troca o e-mail sintético do
  // STAFF (`@{tid}.local` → domínio staff). Gravava `ADMIN_ROLE_CHANGED`, que a
  // tela de super-admins lê como concessão de acesso admin
  // (`useSuperAdminAuditTrail`) — cada STAFF migrado aparecia como admin.
  STAFF_EMAIL_MIGRATED: 'STAFF_EMAIL_MIGRATED',
  // #937 (2026-10-15) — disparo MANUAL do simulador de uso dos tenants de
  // teste (`runTestTenantUsageSimulation`). O scheduler de 2 em 2 h não audita:
  // é o mesmo efeito sem pessoa por trás.
  TEST_TENANT_SIMULATION_RUN: 'TEST_TENANT_SIMULATION_RUN',
} as const;
export type AuditAction = (typeof AuditAction)[keyof typeof AuditAction];
export const ALL_AUDIT_ACTIONS = Object.values(AuditAction) as [AuditAction, ...AuditAction[]];

/**
 * Tipos de alvo de uma ação auditada. `targetId` é interpretado conforme
 * `targetType`.
 */
export const AuditTargetType = {
  TENANT: 'TENANT',
  USER: 'USER',
  USER_DATA_EXPORT: 'USER_DATA_EXPORT',
  /** Sprint 13 — token OOB para confirmação de encerramento self-service. */
  TENANT_DELETION_TOKEN: 'TENANT_DELETION_TOKEN',
  BILLING: 'BILLING',
  // Sprint 16
  INVOICE: 'INVOICE',
  PAYMENT: 'PAYMENT',
  PAYMENT_METHOD: 'PAYMENT_METHOD',
  BILLING_SETTINGS: 'BILLING_SETTINGS',
  PLAN_PRICE: 'PLAN_PRICE',
  // Sprint 4.x — alvo de operações a nível de sistema (não escopadas a um
  // tenant), como o backfill de agregações. `targetId` carrega o descritor
  // da operação (ex.: "posthog:20260101-20260131").
  SYSTEM: 'SYSTEM',
  /** Issue #263 — integração da API pública. `targetId` = keyId. */
  API_KEY: 'API_KEY',
  /** #655 — `targetId` = a plataforma (`ios`, `android`, `web`, `admin`). */
  APP_VERSION_CONFIG: 'APP_VERSION_CONFIG',
  /** #679 9.13 — `targetId` = `terms` (o doc em `/appConfig`). */
  TERMS_CONFIG: 'TERMS_CONFIG',
  /** #1078 — `targetId` = `regions` (o doc em `/config`). */
  REGIONS_CONFIG: 'REGIONS_CONFIG',
  /** #795 — `targetId` = id do doc em `/finance_accounts`. */
  FINANCE_ACCOUNT: 'FINANCE_ACCOUNT',
  /** #795 — `targetId` = id do doc em `/finance_entries`. */
  FINANCE_ENTRY: 'FINANCE_ENTRY',
  /** #795 — `targetId` = id da sessão em `/finance_imports`. */
  FINANCE_IMPORT: 'FINANCE_IMPORT',
} as const;
// Sprint 18: BRANCH/BRANCH_MEMBER removidos do enum. Logs históricos com
// esses valores continuam legíveis no Firestore (string livre); apenas
// o filtro UI no admin perde essas opções.
export type AuditTargetType = (typeof AuditTargetType)[keyof typeof AuditTargetType];
export const ALL_AUDIT_TARGET_TYPES = Object.values(AuditTargetType) as [AuditTargetType, ...AuditTargetType[]];

/**
 * Locales suportados em emails transacionais: os completos de
 * `FULL_LOCALES` (`schemas.ts`) — regional cai na base (`resolveEmailLocale`).
 *
 * Origem: Sprint 8 — sem issue (templates React Email).
 *
 * Usado por: `EmailLocaleSchema` (`email.ts`), templates em
 * `apps/functions/src/email/templates/**` (`Record<EmailLocale, …>`),
 * `apps/functions/src/email/preview/main.ts`, iOS pelo contrato gerado.
 *
 * Dicas: locale novo aqui faz o `tsc` reprovar todo template sem o bloco
 * dele — é o que garante que nenhum e-mail sai sem tradução.
 *
 * Histórico:
 *  - 2026-10-13 11:00 +0200 — (#86) `de`.
 */
export const EmailLocale = {
  PT_BR: 'pt-BR',
  EN: 'en',
  ES: 'es',
  IT: 'it',
  FR: 'fr',
  DE: 'de',
} as const;
export type EmailLocale = (typeof EmailLocale)[keyof typeof EmailLocale];
export const ALL_EMAIL_LOCALES = Object.values(EmailLocale) as [EmailLocale, ...EmailLocale[]];

/**
 * IDs dos templates transacionais. Cada ID corresponde a um template React
 * Email em `apps/functions/src/email/templates/<templateId>/` e a um
 * schema Zod de `data` (input variables) em `email.ts`. PRs B/C/D
 * adicionam os templates conforme escopo da Sprint 8.
 */
export const EmailTemplateId = {
  ONBOARDING_WELCOME: 'ONBOARDING_WELCOME',
  // `ONBOARDING_VERIFY_EMAIL` saiu em 2026-10-17 (#937) com o callable
  // `sendVerificationEmail`, seu único remetente. Logs antigos em `emailLogs`
  // ainda trazem o id; o admin lê `templateId` como string livre.
  ONBOARDING_TENANT_INVITE: 'ONBOARDING_TENANT_INVITE',
  // #679 F1 — boas-vindas do OWNER de uma venda assistida pelo CRM, com o
  // link de definição de senha. Sai no PAGAMENTO, não na criação do tenant:
  // antes disso o cliente não entra, e o link expira em ~1 h.
  SALE_WELCOME: 'SALE_WELCOME',
  // #1047 (2026-10-27 18:06 +0100) — e-mail de equipamentos ao OWNER: qual
  // impressora/leitor/tag comprar, que só recomendamos (sem parceria com
  // fornecedor) e que ele responda com a cidade, ou com marca/modelo/foto do
  // que já tem. Sai no início do self-service, na 1ª cobrança paga depois do
  // trial e no pagamento da venda assistida (`EquipmentEmailStage`).
  ONBOARDING_EQUIPMENT: 'ONBOARDING_EQUIPMENT',
  // #942 (2026-10-12 12:10 +0200) — código de 6 dígitos que confirma o e-mail
  // de uma conta e-mail/senha antes da assinatura self-service do iOS
  // (`requestEmailOtp`). Substituiu o link do extinto `ONBOARDING_VERIFY_EMAIL`: o
  // app não abre link do Firebase, o usuário digita o código na tela.
  EMAIL_OTP_CODE: 'EMAIL_OTP_CODE',
  // Billing — Sprint 16
  INVOICE_CREATED: 'INVOICE_CREATED',
  INVOICE_DUE_SOON: 'INVOICE_DUE_SOON',
  INVOICE_OVERDUE: 'INVOICE_OVERDUE',
  PAYMENT_SUCCEEDED: 'PAYMENT_SUCCEEDED',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  // Sprint 5 — cobrança recorrente exige ação do cliente (3DS/SCA): a
  // tentativa off-session falhou pedindo autenticação. Notifica com link
  // (`hosted_invoice_url`) para concluir antes de virar inadimplência.
  PAYMENT_ACTION_REQUIRED: 'PAYMENT_ACTION_REQUIRED',
  BILLING_SUSPENDED: 'BILLING_SUSPENDED',
  BILLING_REACTIVATED: 'BILLING_REACTIVATED',
  ACCOUNT_EMAIL_CHANGED: 'ACCOUNT_EMAIL_CHANGED',
  ACCOUNT_PASSWORD_CHANGED: 'ACCOUNT_PASSWORD_CHANGED',
  ACCOUNT_NEW_DEVICE_LOGIN: 'ACCOUNT_NEW_DEVICE_LOGIN',
  OPS_TENANT_USER_INVITED: 'OPS_TENANT_USER_INVITED',
  OPS_SUSPECTED_DATA_RESET: 'OPS_SUSPECTED_DATA_RESET',
  BI_ALERT_HIGH: 'BI_ALERT_HIGH',
  // Sprint 19 — lifecycle do tenant (soft-delete reversível).
  // `tenant_deleted` é enviado ao OWNER quando super-admin encerra a
  // organização; `tenant_restored` é o follow-up quando a operação é
  // revertida em até 30 dias (raro — coordenado com support).
  LIFECYCLE_TENANT_DELETED: 'LIFECYCLE_TENANT_DELETED',
  LIFECYCLE_TENANT_RESTORED: 'LIFECYCLE_TENANT_RESTORED',
  // Sprint 13 — LGPD/GDPR. Notifica o titular quando o ZIP de export
  // está pronto, com signed URL (validade 7 dias) e lista de
  // categorias incluídas.
  PRIVACY_DATA_EXPORT_READY: 'PRIVACY_DATA_EXPORT_READY',
  // Sprint 13 PR C — soft-delete da conta do titular: notifica
  // confirmação de exclusão (com prazo de 30d e como reverter) e
  // restore de conta. `tenant_delete_confirmation` carrega link OOB
  // (válido 24h) para confirmação self-service do encerramento da
  // própria organização pelo OWNER.
  PRIVACY_ACCOUNT_DELETION_REQUESTED: 'PRIVACY_ACCOUNT_DELETION_REQUESTED',
  PRIVACY_ACCOUNT_RESTORED: 'PRIVACY_ACCOUNT_RESTORED',
  PRIVACY_TENANT_DELETE_CONFIRMATION: 'PRIVACY_TENANT_DELETE_CONFIRMATION',
  // Sprint 5 — paywall com gateway. `subscription_started` é enviado quando
  // o checkout fecha (webhook do gateway); `mbg_refund_issued` confirma o
  // estorno automático ao encerrar conta dentro da janela MBG; `refund_issued`
  // é o caso geral (super-admin emite refund manual).
  BILLING_SUBSCRIPTION_STARTED: 'BILLING_SUBSCRIPTION_STARTED',
  BILLING_MBG_REFUND_ISSUED: 'BILLING_MBG_REFUND_ISSUED',
  BILLING_REFUND_ISSUED: 'BILLING_REFUND_ISSUED',
  // Sprint 5 PR F — cancelamento (end-of-period) e reativação. Nasceram para
  // os callables self-service `requestSubscriptionCancel` e
  // `reactivateSubscription`, que saíram do servidor em 2026-09-24 (#714);
  // hoje quem dispara é o pós-venda do CRM — `crmCancelSaleSubscription` e
  // `crmReactivateSaleSubscription` (`billing/postSale/cancellation.ts`, #737).
  BILLING_SUBSCRIPTION_CANCELLED: 'BILLING_SUBSCRIPTION_CANCELLED',
  BILLING_SUBSCRIPTION_REACTIVATED: 'BILLING_SUBSCRIPTION_REACTIVATED',
  // #289 — o cliente abriu o checkout e nao concluiu (sessao expirada ou QR do
  // Pix vencido). Leva o link de recuperacao e explica que, no Pix Automatico,
  // o app do banco pede AUTORIZACAO de debito recorrente — nao um Pix comum de
  // pagar. Foi exatamente onde a primeira cliente travou.
  BILLING_CHECKOUT_ABANDONED: 'BILLING_CHECKOUT_ABANDONED',
  // #737 (F5 da #679) — o consultor acrescentou um módulo numa assinatura em
  // Pix Automático: a proporção do ciclo atual vai num link avulso (o Pix não
  // prorrateia dentro do mandato — 9.24). Quando o total novo pede mandato
  // novo, o mesmo link autoriza, e o e-mail avisa que o banco vai pedir isso.
  BILLING_ADDON_PRORATION_LINK: 'BILLING_ADDON_PRORATION_LINK',
  // #737 (F5 da #679) — aviso ao cliente de mudança feita pelo consultor que
  // não vale na hora: aumento de preço (30 dias de antecedência, 9.17) e troca
  // de ciclo. Em Pix, quando o mandato atual não cobre o plano novo, leva o
  // link de autorização do mandato novo.
  BILLING_PLAN_CHANGE_NOTICE: 'BILLING_PLAN_CHANGE_NOTICE',
  // Alerta interno (super-admins) do monitor diário de saúde do `daily_ops`
  // (`monitorDailyOpsHealth`): disparado quando a reconciliação detecta que
  // os contadores agregados divergem da contagem real das etiquetas
  // (drift, doc ausente com atividade, ou corrupção estrutural de chaves).
  ALERT_DAILY_OPS_HEALTH: 'ALERT_DAILY_OPS_HEALTH',
  // Notificação de OPERAÇÃO para super-admins (apps/admin → tela de
  // preferências). Template genérico: `title`/`lines` pré-formatados pelo
  // trigger; `event` indica qual evento de negócio disparou (novo tenant,
  // pagamento, cancelamento/refund, suspensão). Fan-out por preferência em
  // `notifyAdmins` (lib/adminNotify.ts).
  ADMIN_OPS_NOTIFICATION: 'ADMIN_OPS_NOTIFICATION',
  // Aviso ao TITULAR de que o acesso dele foi desligado a pedido dele mesmo
  // (`deactivateMyAccount`). Mesma classe de `ACCOUNT_PASSWORD_CHANGED`: sai
  // sempre, porque se não foi a pessoa, é por aqui que ela descobre. E é o
  // único artefato que sobrevive à ação — o app cai para o login logo depois,
  // então é neste e-mail que mora a instrução de como voltar.
  ACCOUNT_DEACTIVATED: 'ACCOUNT_DEACTIVATED',
  // Aviso ao ETIQUETACHEF de que alguém pediu a exclusão da conta. Sai
  // SEMPRE: o pedido é churn, e a exclusão definitiva é feita à mão pela
  // equipe — sem este e-mail o pedido não chega a ninguém e a conta fica só
  // com o acesso desligado, para sempre.
  OPS_ACCOUNT_DELETION_REQUESTED: 'OPS_ACCOUNT_DELETION_REQUESTED',
  // Relatório interno diário (`dailyInactiveTenantsReport`): tenants
  // pagantes que passaram 3 dias civis sem imprimir nenhuma etiqueta.
  // Destinatário fixo admin@etiquetachef.com; só é enviado quando há ao
  // menos um tenant na lista.
  REPORT_INACTIVE_TENANTS: 'REPORT_INACTIVE_TENANTS',
  // #664 — relatório do módulo Relatórios (`MOD_REPORTS`), pedido pelo
  // dono/gerente e gerado pela fila (`onReportJobCreated`). Um id para os 21
  // relatórios: o payload carrega o `ReportDocument` (blocos) e o template só
  // desenha blocos. E-mail de CLIENTE, moldura nos 5 locales; o relatório
  // completo vai em PDF anexado.
  REPORT_OPERATIONS: 'REPORT_OPERATIONS',
  // #864 — relatório interno diário de uso de memória por Cloud Function
  // (`dailyMemoryReport`). Destinatário fixo admin@etiquetachef.com; sai
  // TODO dia (o owner pediu acompanhamento, não só alerta), só em pt-BR.
  INFRA_MEMORY_REPORT: 'INFRA_MEMORY_REPORT',
  // #886 — resumo interno diário do monitor de padrão de uso
  // (`detectTenantUsageAnomalies`): clientes que saíram da própria rotina
  // (queda/excesso semanal, dia habitual sem impressão). Destinatário fixo
  // admin@etiquetachef.com; só sai quando há desvio novo; só em pt-BR.
  REPORT_USAGE_ANOMALIES: 'REPORT_USAGE_ANOMALIES',
} as const;
export type EmailTemplateId = (typeof EmailTemplateId)[keyof typeof EmailTemplateId];
export const ALL_EMAIL_TEMPLATE_IDS = Object.values(EmailTemplateId) as [EmailTemplateId, ...EmailTemplateId[]];

/**
 * Eventos do SendGrid Event Webhook que persistimos em `/emailLogs`.
 * `processed` e `delivered` confirmam fluxo; `bounce`/`dropped`/`spamreport`
 * disparam bloqueio (PR D). `open`/`click` ficam para métricas.
 */
export const EmailEventType = {
  PROCESSED: 'processed',
  DELIVERED: 'delivered',
  OPEN: 'open',
  CLICK: 'click',
  BOUNCE: 'bounce',
  DROPPED: 'dropped',
  DEFERRED: 'deferred',
  BLOCKED: 'blocked',
  SPAMREPORT: 'spamreport',
  UNSUBSCRIBE: 'unsubscribe',
  GROUP_UNSUBSCRIBE: 'group_unsubscribe',
  GROUP_RESUBSCRIBE: 'group_resubscribe',
} as const;
export type EmailEventType = (typeof EmailEventType)[keyof typeof EmailEventType];
export const ALL_EMAIL_EVENT_TYPES = Object.values(EmailEventType) as [EmailEventType, ...EmailEventType[]];

// ---------- Sprint 35 — Pricing platform (SKUs, capabilities, add-ons) ----------

/**
 * SKU de subscription item — `BASE` (plano obrigatório) e os add-ons
 * conhecidos no momento do release. Cada SKU vira N Stripe Products
 * (1 por billing cycle) + 1 doc `/config/planPrices/{sku}_{currency}`
 * no Firestore.
 *
 * **PR G1 (2026-05-25)**: SKUs de add-on passaram a ser **livres em
 * runtime** (super-admin cria via `/billing/addons` sem deploy). A
 * validação é regex (`SkuSchema` em `schemas.ts` —
 * `^[A-Z][A-Z0-9_]{2,30}$`), e o tipo `Sku` virou `string`. Estas
 * constantes aqui ficam para o código que precisa referenciar o SKU
 * `BASE` por nome (`Sku.BASE`) — guards do tipo
 * "BASE não é add-on opcional".
 */
export const Sku = {
  BASE: 'BASE',
  // `SUPPLIER_INTEGRATION` saiu na #679 F4 (9.17): nunca foi vendido, não há
  // doc de catálogo nem assinatura com ele, e o módulo de compras é o
  // `MOD_PURCHASES` (D19), sem migração entre os dois.

  /**
   * Módulos da #679 (D19/D21) que o **código** precisa nomear. O catálogo é
   * de 24 SKUs, e os outros 17 são **dado**, criados por `createAddon` sem
   * deploy — só entram aqui os que alguma linha de código consulta: os seis de
   * operação, porque cada um gateia um item de menu nas três plataformas, e o
   * `MOD_PRINT_TSPL`, porque é o concedido da D22.
   *
   * Nomear os 24 aqui seria transformar em código o que a arquitetura fez
   * questão de manter como dado — e o primeiro efeito seria precisar de um
   * deploy para vender uma linguagem de impressão.
   */
  MOD_PRINT_TSPL: 'MOD_PRINT_TSPL',
  /**
   * Balança Bluetooth (#341) — **APOSENTADA para sempre** (owner, 2026-10-27 12:43 +0100, #1043): o
   * `MOD_SCALE_AI` (câmera lê o visor) a substitui. O valor fica no enum para documentos antigos
   * continuarem válidos, mas é **sempre desligado para todos**: está em `RETIRED_SKUS` (`billing.ts`), e
   * `readIapBundleSkus`, `createAddon`, `enableAddon` e `grantModuleToTenants` recusam concedê-lo. Nenhum
   * menu o consulta mais (o item do Mais virou `MOD_SCALE_AI`). Não existe código BLE de balança.
   */
  MOD_SCALE: 'MOD_SCALE',
  /**
   * Balança Inteligente (#1014/#995, 2026-10-21): ler o PESO no visor da
   * balança pela câmera do iPhone e digitá-lo no campo de quantidade do editor
   * de etiqueta. Desde 2026-10-26 (#1030, decisão do owner) não reconhece mais
   * o produto nem grava nada: as rules de fotos, capturas e perfis saíram, e
   * `firebase/test/rules/scale_ai_removed*.test.ts` prova o deny. Gateia só o
   * ícone de scan no app (`ScaleAiAccess`). Venda pausada (#679 D26): o SKU
   * existe como dado no catálogo (`docs/catalog`), esta constante só nomeia o
   * que o código consulta.
   */
  MOD_SCALE_AI: 'MOD_SCALE_AI',
  MOD_SCANNER: 'MOD_SCANNER',
  MOD_TEMPERATURE: 'MOD_TEMPERATURE',
  MOD_CHECKLISTS: 'MOD_CHECKLISTS',
  MOD_REPORTS: 'MOD_REPORTS',
  MOD_PURCHASES: 'MOD_PURCHASES',
  /**
   * Etiquetas inteligentes (#900/#901, 2026-10-09): gravar e ler a etiqueta em
   * tag NFC (capacidade ≥ a da NTAG213; conteúdo nunca maior que ela). Gateia o
   * callable `bindNfcTag` (servidor), a folha "Gravar NFC" e a leitura em
   * segundo plano da tela Etiquetas (Android) ou o botão "Ler NFC" (iOS, #905;
   * no iOS a capability é string crua, `NfcEntryModel.capability`). Ler o id vindo de
   * um link ou do despacho da tag pelo sistema NÃO é do módulo — é igual a
   * escanear o QR.
   */
  MOD_NFC_TAGS: 'MOD_NFC_TAGS',
} as const;
/**
 * Tipo `Sku` é `string` — qualquer identificador que passa pelo regex
 * `SkuSchema` é válido (super-admin cria add-ons sem deploy). Para
 * checar SKUs *bem conhecidos* em código, compare com `Sku.BASE` etc.
 */
export type Sku = string;

/**
 * Capabilities derivadas dos `Subscription.items[]`, calculadas no reconciler
 * de webhook. O campo `capabilities` do documento é ABERTO (`CapabilityKeySchema`,
 * um SKU por módulo); esta lista fechada é só a das capabilities que o código
 * nomeia — hoje só `BASE_ACTIVE` (o `SUPPLIER_INTEGRATION` saiu na #679 F4).
 */
export const Capability = {
  BASE_ACTIVE: 'BASE_ACTIVE',
} as const;
export type Capability = (typeof Capability)[keyof typeof Capability];
export const ALL_CAPABILITIES = Object.values(Capability) as [Capability, ...Capability[]];

/**
 * Categoria de isenção lifetime-free. Cupom Stripe `LIFETIME_FREE`
 * (`percent_off: 100, duration: 'forever'`) é o mecanismo; categoria +
 * `reason` ficam no `Subscription.discount` para BI/auditoria.
 * `OTHER` exige `reason` preenchido (guard no callable `applyLifetimeFree`).
 */
export const LifetimeFreeCategory = {
  FOUNDER: 'FOUNDER',
  EMPLOYEE: 'EMPLOYEE',
  BETA_TESTER: 'BETA_TESTER',
  PARTNER: 'PARTNER',
  COMPENSATION: 'COMPENSATION',
  DEMO: 'DEMO',
  OTHER: 'OTHER',
} as const;
export type LifetimeFreeCategory = (typeof LifetimeFreeCategory)[keyof typeof LifetimeFreeCategory];
export const ALL_LIFETIME_FREE_CATEGORIES = Object.values(LifetimeFreeCategory) as [
  LifetimeFreeCategory,
  ...LifetimeFreeCategory[],
];

/**
 * Status de uma PJ emissora de fatura em `/config/sellerEntities/{id}`.
 * MVP da Sprint 35: 1 entry `etiquetachef-srl` (P.IVA italiana, cap
 * €100.000). Futuro: Dubai LLC, Singapore Pte etc. Cada `MarketConfig`
 * aponta para qual seller vende no país.
 */
export const SellerEntityStatus = {
  ACTIVE: 'ACTIVE',
  PENDING: 'PENDING',
  ARCHIVED: 'ARCHIVED',
} as const;
export type SellerEntityStatus = (typeof SellerEntityStatus)[keyof typeof SellerEntityStatus];
export const ALL_SELLER_ENTITY_STATUSES = Object.values(SellerEntityStatus) as [
  SellerEntityStatus,
  ...SellerEntityStatus[],
];

/**
 * Provider fiscal. `STRIPE_HOSTED` = invoice PDF do Stripe (sem nota fiscal
 * local) — a venda pela PJ italiana. `ASAAS_NFSE` = NFS-e emitida pelo
 * próprio Asaas na confirmação do pagamento (#748 G6), pela PJ brasileira.
 * `PADDLE_MOR` = Paddle Merchant of Record (UE, não implementado). `OFF` =
 * mercado bloqueado para venda.
 *
 * **O fiscal acompanha o gateway da venda, não o país**: um cliente BR
 * vendido pelo Stripe IT não recebe NFS-e. Por isso a assinatura grava o seu
 * (`Subscription.fiscal_provider`) no momento da venda; o do mercado é só o
 * padrão do gateway padrão.
 */
export const FiscalProvider = {
  STRIPE_HOSTED: 'STRIPE_HOSTED',
  /**
   * NFS-e pelo Asaas (#748 G6). Gravado na assinatura pelo link da venda do
   * Asaas (A3, #752), junto com `external_provider: asaas`.
   *
   * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas, G6).
   *
   * Usado por: Functions — `billing/asaas/saleLink.ts` (grava na venda),
   * `billing/providers/asaas.ts`, `lib/billing/unpaidAsaasSale.ts`; contrato
   * gerado (`packages/types/contract/schemas.json`, cópia no iOS).
   *
   * Arquitetura: é **rótulo** da venda, não gatilho. Quem decide emitir nota
   * é o gateway do PAGAMENTO (#754, owner 2026-09-27): todo pagamento
   * confirmado do Asaas gera NFS-e (`billing/asaas/nfse.ts`, chamado só pelo
   * webhook do Asaas) e pagamento de outro gateway nunca gera. Por isso o
   * B-L3 da #752 (o `ASAAS_NFSE` que sobra no doc depois de o link seguinte
   * trocar a venda para o Stripe) não emite nota errada: o pagamento do
   * Stripe nunca passa pelo emissor.
   */
  ASAAS_NFSE: 'ASAAS_NFSE',
  PADDLE_MOR: 'PADDLE_MOR',
  OFF: 'OFF',
} as const;
export type FiscalProvider = (typeof FiscalProvider)[keyof typeof FiscalProvider];
export const ALL_FISCAL_PROVIDERS = Object.values(FiscalProvider) as [FiscalProvider, ...FiscalProvider[]];

/**
 * Regime tributário de uma PJ brasileira (a que vende pelo Asaas e emite NFS-e).
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (dados fiscais da `etiquetachef-br`).
 *
 * Usado por: `SellerEntityBrFiscalSchema.tax_regime` (`brFiscal.ts`); admin —
 * editor de entidades vendedoras. `SIMPLES_NACIONAL` e `MEI` viram
 * `simplesNacional: true` no `POST /v3/fiscalInfo` do Asaas.
 */
export const BrTaxRegime = {
  SIMPLES_NACIONAL: 'SIMPLES_NACIONAL',
  MEI: 'MEI',
  LUCRO_PRESUMIDO: 'LUCRO_PRESUMIDO',
  LUCRO_REAL: 'LUCRO_REAL',
} as const;
export type BrTaxRegime = (typeof BrTaxRegime)[keyof typeof BrTaxRegime];
export const ALL_BR_TAX_REGIMES = Object.values(BrTaxRegime) as [BrTaxRegime, ...BrTaxRegime[]];

/**
 * Estado da NFS-e de uma fatura (`Invoice.nfse.status`).
 *
 * Origem: #754 — 2026-09-27 22:24 +0200 (A5 do Asaas, NFS-e por pagamento).
 *
 * Por quê: os sete primeiros são os `status` da nota no Asaas, medidos no
 * sandbox em 2026-09-27 (`SCHEDULED` → `SYNCHRONIZED` → `AUTHORIZED` em ~40 s;
 * reembolso → `PROCESSING_CANCELLATION` → `CANCELED` em ~30 s). Os dois
 * últimos são nossos:
 *  - `REQUEST_FAILED` — o nosso pedido de nota ao Asaas falhou (config fiscal
 *    ausente, 4xx/5xx). A nota não existe lá; a reentrega do evento refaz.
 *  - `UNKNOWN` — status que esta versão não conhece. É GRAVADO quando o Asaas
 *    manda um status novo num `INVOICE_*` (`AsaasInvoiceSchema` cai em
 *    `UNKNOWN`), e é também o fallback de leitura — um valor novo não pode
 *    derrubar a tela de faturas.
 *
 * Usado por: `InvoiceNfseSchema` (`schemas.ts`); Functions
 * `billing/asaas/nfse.ts` (grava) e `webhooks/asaas/asaasHandlers.ts`
 * (`INVOICE_*`); web `packages/ui-shared/src/billing/InvoiceTable.tsx`,
 * `apps/app` (faturas) e `apps/admin` (faturas do tenant).
 *
 * Issues: #754 (A5), #748 (épico, G6).
 */
export const NfseStatus = {
  SCHEDULED: 'SCHEDULED',
  SYNCHRONIZED: 'SYNCHRONIZED',
  AUTHORIZED: 'AUTHORIZED',
  PROCESSING_CANCELLATION: 'PROCESSING_CANCELLATION',
  CANCELED: 'CANCELED',
  CANCELLATION_DENIED: 'CANCELLATION_DENIED',
  ERROR: 'ERROR',
  REQUEST_FAILED: 'REQUEST_FAILED',
  UNKNOWN: 'UNKNOWN',
} as const;
export type NfseStatus = (typeof NfseStatus)[keyof typeof NfseStatus];
export const ALL_NFSE_STATUSES = Object.values(NfseStatus) as [NfseStatus, ...NfseStatus[]];

/**
 * Modo de cálculo do desconto anual em `/config/planPrices/{sku}_{currency}`.
 * `DISCOUNT_PCT`: `yearly = monthly × 12 × (1 - pct)`. `MANUAL`: owner
 * digita absoluto. Range 0%-80% (warning visual acima de 50%).
 */
export const YearlyDiscountMode = {
  DISCOUNT_PCT: 'DISCOUNT_PCT',
  MANUAL: 'MANUAL',
} as const;
export type YearlyDiscountMode = (typeof YearlyDiscountMode)[keyof typeof YearlyDiscountMode];
export const ALL_YEARLY_DISCOUNT_MODES = Object.values(YearlyDiscountMode) as [
  YearlyDiscountMode,
  ...YearlyDiscountMode[],
];

/**
 * Escopo de uma `Promotion` em `/promotions/{id}`. `PUBLIC` = aceita
 * via `allow_promotion_codes` no Checkout (só no anual por design);
 * `NOMINAL` = aplicado pelo super-admin a um tenant específico;
 * `LIFETIME_FREE_TEMPLATE` é o cupom mestre 100% off forever reusado
 * pelo callable `applyLifetimeFree`.
 */
export const PromotionScope = {
  PUBLIC: 'PUBLIC',
  NOMINAL: 'NOMINAL',
  LIFETIME_FREE_TEMPLATE: 'LIFETIME_FREE_TEMPLATE',
} as const;
export type PromotionScope = (typeof PromotionScope)[keyof typeof PromotionScope];
export const ALL_PROMOTION_SCOPES = Object.values(PromotionScope) as [PromotionScope, ...PromotionScope[]];

/**
 * Duração de um cupom Stripe (`once` = primeira invoice; `repeating` =
 * N meses via `duration_in_months`; `forever` = todas as invoices até
 * remoção). Espelha Stripe API 1:1.
 */
export const CouponDuration = {
  ONCE: 'once',
  REPEATING: 'repeating',
  FOREVER: 'forever',
} as const;
export type CouponDuration = (typeof CouponDuration)[keyof typeof CouponDuration];
export const ALL_COUPON_DURATIONS = Object.values(CouponDuration) as [CouponDuration, ...CouponDuration[]];

/**
 * Tipo de ator que disparou um evento em `/audit/billing/{eventId}`.
 * `USER` = OWNER do tenant agindo self-service; `SUPER_ADMIN` = staff
 * EtiquetaChef; `WEBHOOK` = handler de webhook Stripe/Paddle; `SYSTEM` =
 * helper interno (ex: `cancelSubscription` chamado por outro callable);
 * `SCHEDULED_JOB` = cron (dailyBillingTick, pruneAuditIpHashes, etc.).
 */
export const AuditActorType = {
  USER: 'USER',
  SUPER_ADMIN: 'SUPER_ADMIN',
  WEBHOOK: 'WEBHOOK',
  SYSTEM: 'SYSTEM',
  SCHEDULED_JOB: 'SCHEDULED_JOB',
} as const;
export type AuditActorType = (typeof AuditActorType)[keyof typeof AuditActorType];
export const ALL_AUDIT_ACTOR_TYPES = Object.values(AuditActorType) as [AuditActorType, ...AuditActorType[]];

/**
 * Ações de `/audit/billing/{eventId}` (Sprint 35). Coleção separada do
 * `/audit/{eventId}` legacy (cuja schema `AuditAction` cobre operações
 * de tenant/user/branch). Aqui só vão eventos financeiros — retenção
 * legal 10 anos, `actorIpHash` pruned em 90d.
 */
export const AuditBillingAction = {
  // Subscription lifecycle
  SUBSCRIPTION_CREATED: 'SUBSCRIPTION_CREATED',
  SUBSCRIPTION_CANCEL_SCHEDULED: 'SUBSCRIPTION_CANCEL_SCHEDULED',
  SUBSCRIPTION_CANCELLED: 'SUBSCRIPTION_CANCELLED',
  SUBSCRIPTION_REACTIVATED: 'SUBSCRIPTION_REACTIVATED',
  SUBSCRIPTION_SUSPENDED: 'SUBSCRIPTION_SUSPENDED',
  SUBSCRIPTION_MIGRATED_TO_ITEMS: 'SUBSCRIPTION_MIGRATED_TO_ITEMS',
  // Reconciliação manual do doc local com a verdade do gateway (super-admin)
  SUBSCRIPTION_RECONCILED: 'SUBSCRIPTION_RECONCILED',
  // Items
  ITEM_ADDED: 'ITEM_ADDED',
  ITEM_REMOVED: 'ITEM_REMOVED',
  ITEM_REMOVAL_SCHEDULED: 'ITEM_REMOVAL_SCHEDULED',
  /** Cliente desfez a remoção agendada antes do gateway executá-la (#405). */
  ITEM_REMOVAL_CANCELLED: 'ITEM_REMOVAL_CANCELLED',
  /**
   * Módulo CONCEDIDO, sem cobrança e sem contrapartida no gateway (#679 D22).
   * Ação própria, e não `ITEM_ADDED`, porque as duas respondem perguntas
   * diferentes numa auditoria de dinheiro: `ITEM_ADDED` é uma venda, esta é
   * uma decisão nossa. Confundi-las faria a concessão aparecer no mesmo
   * relatório que a receita.
   */
  ITEM_GRANTED: 'ITEM_GRANTED',
  /** Recomputação de `capabilities[]` a partir de `items[]` (#679 FC). */
  CAPABILITIES_BACKFILLED: 'CAPABILITIES_BACKFILLED',
  // Cycle change
  CYCLE_CHANGE_REQUESTED: 'CYCLE_CHANGE_REQUESTED',
  CYCLE_CHANGE_APPLIED: 'CYCLE_CHANGE_APPLIED',
  /** Consultor desfez uma troca de ciclo agendada (#737). */
  CYCLE_CHANGE_CANCELLED: 'CYCLE_CHANGE_CANCELLED',
  // Pós-venda pelo CRM (#737, F5 da #679)
  /** Redução de preço de um item — vale na próxima fatura, sem aviso. O
   * aumento usa `PRICE_INCREASE_NOTIFIED`/`_APPLIED` (aviso de 30 dias). */
  ITEM_PRICE_CHANGED: 'ITEM_PRICE_CHANGED',
  /** Consultor desistiu de um aumento agendado antes de ele valer. */
  ITEM_PRICE_CHANGE_CANCELLED: 'ITEM_PRICE_CHANGE_CANCELLED',
  /** Link avulso da proporção de um módulo novo em assinatura Pix (9.24). */
  ADDON_PRORATION_LINK_CREATED: 'ADDON_PRORATION_LINK_CREATED',
  ADDON_PRORATION_PAID: 'ADDON_PRORATION_PAID',
  /** Proporção não paga no prazo: o item sai da assinatura (passe 7). */
  ADDON_PRORATION_EXPIRED: 'ADDON_PRORATION_EXPIRED',
  /** Mandato Pix novo passou a ser o método padrão da assinatura. */
  MANDATE_REPLACED: 'MANDATE_REPLACED',
  // Cupons + promo codes
  COUPON_CREATED: 'COUPON_CREATED',
  COUPON_DELETED: 'COUPON_DELETED',
  COUPON_APPLIED: 'COUPON_APPLIED',
  COUPON_REMOVED: 'COUPON_REMOVED',
  // Sincronização do espelho /promotions com o(s) PSP(s) (hoje Stripe via
  // syncCoupons; futuramente Paddle/dLocal/EBANX). Importa cupons criados
  // direto no painel do PSP.
  COUPONS_SYNCED: 'COUPONS_SYNCED',
  PROMOTION_CODE_CREATED: 'PROMOTION_CODE_CREATED',
  PROMOTION_CODE_REDEEMED: 'PROMOTION_CODE_REDEEMED',
  // Isenções
  LIFETIME_FREE_APPLIED: 'LIFETIME_FREE_APPLIED',
  LIFETIME_FREE_REMOVED: 'LIFETIME_FREE_REMOVED',
  // #743 (9.18) — troca de tipo comercial pela matriz: aplicada, agendada
  // (DIRECT→PARTNER no fim do período, PARTNER→DIRECT ao pagar) e desfeita.
  COMMERCIAL_MODE_CHANGED: 'COMMERCIAL_MODE_CHANGED',
  COMMERCIAL_MODE_CHANGE_SCHEDULED: 'COMMERCIAL_MODE_CHANGE_SCHEDULED',
  COMMERCIAL_MODE_CHANGE_CANCELLED: 'COMMERCIAL_MODE_CHANGE_CANCELLED',
  // #743 — o parceiro (ou um ADMIN do CRM) reescreveu a composição do cliente dele.
  PARTNER_COMPOSITION_CHANGED: 'PARTNER_COMPOSITION_CHANGED',
  // Pagamento
  PAYMENT_RECEIVED: 'PAYMENT_RECEIVED',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  PAYMENT_METHOD_UPDATED: 'PAYMENT_METHOD_UPDATED',
  // #289 — falha ANTES da assinatura ativar. Sem invoice não há
  // `invoice.payment_failed`, então esses dois eram invisíveis.
  CHECKOUT_ABANDONED: 'CHECKOUT_ABANDONED',
  /** Cliente revogou o mandato do Pix Automático no app do banco. */
  MANDATE_REVOKED: 'MANDATE_REVOKED',
  /**
   * #289 — super-admin gerou um link de pagamento para um tenant pelo console,
   * ou reenviou por e-mail o link que já existia. Fica auditado porque cria
   * uma Checkout Session real no gateway em nome do cliente.
   */
  CHECKOUT_LINK_CREATED: 'CHECKOUT_LINK_CREATED',
  CHECKOUT_LINK_SENT: 'CHECKOUT_LINK_SENT',
  // Refund / MBG
  REFUND_REQUESTED: 'REFUND_REQUESTED',
  REFUND_SUCCEEDED: 'REFUND_SUCCEEDED',
  REFUND_FAILED: 'REFUND_FAILED',
  MBG_TRIGGERED: 'MBG_TRIGGERED',
  MBG_BLOCKED_GUARD: 'MBG_BLOCKED_GUARD',
  // Catálogo
  PRICE_UPDATED: 'PRICE_UPDATED',
  // Sincronização do espelho /config/planPrices com o(s) PSP(s) — preço é
  // editado direto no painel do PSP e puxado por syncPlanPrices.
  PLAN_PRICES_SYNCED: 'PLAN_PRICES_SYNCED',
  ADDON_CREATED: 'ADDON_CREATED',
  ADDON_UPDATED: 'ADDON_UPDATED',
  /** Venda do add-on pausada (`disableAddon`). O nome é histórico: desde a
   * D20 da #679 é pausa reversível, não exclusão — o par é `ADDON_ENABLED`. */
  ADDON_DELETED: 'ADDON_DELETED',
  /** Venda do add-on retomada (`enableAddon`, #679 D20). */
  ADDON_ENABLED: 'ADDON_ENABLED',
  ADDON_ENABLED_COUNTRY: 'ADDON_ENABLED_COUNTRY',
  ADDON_DISABLED_COUNTRY: 'ADDON_DISABLED_COUNTRY',
  // Migração de preço
  PRICE_INCREASE_NOTIFIED: 'PRICE_INCREASE_NOTIFIED',
  PRICE_INCREASE_APPLIED: 'PRICE_INCREASE_APPLIED',
  // Fiscal
  INVOICE_ISSUED: 'INVOICE_ISSUED',
  INVOICE_VOIDED: 'INVOICE_VOIDED',
  FISCAL_DOC_ISSUED: 'FISCAL_DOC_ISSUED',
  FISCAL_DOC_CANCELLED: 'FISCAL_DOC_CANCELLED',
  FISCAL_PROVIDER_ENABLED: 'FISCAL_PROVIDER_ENABLED',
  FISCAL_PROVIDER_DISABLED: 'FISCAL_PROVIDER_DISABLED',
  // Mercados
  MARKET_ENABLED: 'MARKET_ENABLED',
  MARKET_DISABLED: 'MARKET_DISABLED',
  MARKET_PROVIDER_CHANGED: 'MARKET_PROVIDER_CHANGED',
  MARKET_SELLER_CHANGED: 'MARKET_SELLER_CHANGED',
  /** #1078 (F2 da #960) — override de região do país (`config/markets/items/{CC}.region`). */
  MARKET_REGION_CHANGED: 'MARKET_REGION_CHANGED',
  // Seller entities
  SELLER_ENTITY_CREATED: 'SELLER_ENTITY_CREATED',
  SELLER_ENTITY_UPDATED: 'SELLER_ENTITY_UPDATED',
  SELLER_ENTITY_ARCHIVED: 'SELLER_ENTITY_ARCHIVED',
  // Catálogo de PSP (/config/psps) — Sprint 40
  PSP_CONFIG_SET: 'PSP_CONFIG_SET',
  PSP_CONFIG_DELETED: 'PSP_CONFIG_DELETED',
  /** #756 — versão nova de credencial de PSP gravada pelo admin em /psp (sem o valor). */
  PSP_SECRET_SET: 'PSP_SECRET_SET',
  // Guards
  GUARD_BLOCKED: 'GUARD_BLOCKED',
  /**
   * #274 — evento de subscription do gateway descartado por NÃO ser a
   * assinatura que o doc do tenant reivindica (`external_subscription_id`).
   * Nasce do re-checkout duplo (§3.1.6): a sub perdedora segue emitindo
   * `updated`/`deleted`, e processá-los contra o doc da sobrevivente marcava
   * CANCELLED / apagava o `external_subscription_id` de um tenant pagante —
   * paywall com assinatura viva e paga no Stripe.
   *
   * `before.external_subscription_id` guarda o id que o doc tinha;
   * `subscriptionId` é o da sub do evento. Ver
   * `webhooks/handlerContext.subscriptionEventMatchesDoc`.
   */
  SUBSCRIPTION_EVENT_IGNORED: 'SUBSCRIPTION_EVENT_IGNORED',
} as const;
export type AuditBillingAction = (typeof AuditBillingAction)[keyof typeof AuditBillingAction];
export const ALL_AUDIT_BILLING_ACTIONS = Object.values(AuditBillingAction) as [
  AuditBillingAction,
  ...AuditBillingAction[],
];
